/**
 * Core icon library:
 *
 * https://www.figma.com/file/zYke5qihweC8yQSpXpcRO2/%F0%9F%93%9000-OM-CORE-Library?node-id=17%3A175
 */
import React from 'react';
import { ColorCodes, FUTURO_BY_NAME } from '../color';

// ============================================================================
// README
// ----------------------------------------------------------------------------
// The following objects are taken directly from the CORE figma diagram.
// No additions or changes should be made here, except to align with the CORE
// diagram.
//
// Changes to the CORE diagram should go through the process defined in RFC009.
//
// When adding new icons, please try and keep them ordered according to how they
// are found in the figma diagram.
//
// Naming convention based on the layout of the icon library and the naming set
// in place in the spacing reference guide
//
// xs: 12x12
//  s: 16x16
//  m: 20x20
//  l: 24x24
// xl: 44x44
//
// Generally that falls into either of the following schemas:
//
// sizeName
// sizeSubsectionName
//
// Each svg is an object containing both the svg and some additional design
// details such as default color and its size.
//
// You will probably also want to remove the "fill" prop if it exists.
// So that the Icon component can manage color itself.
//
// ============================================================================

// SWEET_ORANGE, STORM and SWAN are the most common colors for icons
// (Being primary, grey and white)
const { SWEET_ORANGE, STORM, STEEL, SWAN, CLOVER, ROSE, SHADOW, GOLD } =
  FUTURO_BY_NAME;

export const SvgPaths = {
  favourites: {
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 17L3.4776 10.9652C2.5248 10.0839 2 8.91026 2 7.66036C2 6.41045 2.5248 5.23678 3.4776 4.35477C5.2536 2.71131 8.0416 2.56353 10 3.9091C11.9584 2.56353 14.7464 2.71131 16.5224 4.35477C17.4752 5.23678 18 6.41045 18 7.66036C18 8.91026 17.4752 10.0839 16.5224 10.9652L10 17ZM15.1644 9.49688C15.7122 8.9902 16 8.34661 16 7.66036C16 6.97401 15.7121 6.33009 15.164 5.82269C14.0968 4.83516 12.3463 4.72358 11.1326 5.55751L10 6.33568L8.86742 5.55751C7.65368 4.72358 5.90315 4.83516 4.83624 5.82245C4.28786 6.33009 4 6.97401 4 7.66036C4 8.34661 4.28776 8.9902 4.83588 9.49715L10 14.2752L15.1644 9.49688Z"
      />
    ),
    viewBox: '0 0 20 20',
  },
  print: {
    svg: (
      <>
        <path
          d="M17.4 16.0003H20.1C20.5971 16.0003 21 15.5526 21 15.0003V9.00028C21 8.44799 20.5971 8.00027 20.1 8.00027H3.9C3.40295 8.00027 3 8.44799 3 9.00028V15.0003C3 15.5526 3.40295 16.0003 3.9 16.0003H6.6"
          stroke="#FF5500"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M6.59961 12H17.3996V20C17.3996 20.5523 16.9967 21 16.4996 21H7.49961C7.00256 21 6.59961 20.5523 6.59961 20V12Z"
          stroke="#FF5500"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M6.59961 4C6.59961 3.44772 7.00256 3 7.49961 3H16.4996C16.9967 3 17.3996 3.44772 17.3996 4V8H6.59961V4Z"
          stroke="#FF5500"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
    viewBox: '0 0 24 24',
  },
  playVideo: {
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2 3.68453C2 2.16641 3.62495 1.20178 4.9577 1.92874L12.8694 6.24419C14.2591 7.00223 14.2591 8.99774 12.8694 9.75578L4.9577 14.0712C3.62496 14.7982 2 13.8336 2 12.3154V3.68453ZM11.9117 7.99999L4 3.68453V12.3154L11.9117 7.99999Z"
      />
    ),
    viewBox: '0 0 16 16',
  },
  googleIcon: {
    svg: (
      <>
        <g clipPath="url(#clip0_1165_3770)">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M17.64 9.70468C17.64 9.0665 17.5827 8.45286 17.4764 7.86377H9V11.3451H13.8436C13.635 12.4701 13.0009 13.4233 12.0477 14.0615V16.3197H14.9564C16.6582 14.7529 17.64 12.4456 17.64 9.70468Z"
            fill="#4285F4"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M9 18.5C11.43 18.5 13.4673 17.6941 14.9564 16.3195L12.0477 14.0613C11.2418 14.6013 10.2109 14.9204 9 14.9204C6.65591 14.9204 4.67182 13.3372 3.96409 11.21H0.957275V13.5418C2.43818 16.4831 5.48182 18.5 9 18.5Z"
            fill="#34A853"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M3.96409 11.2098C3.78409 10.6698 3.68182 10.093 3.68182 9.49983C3.68182 8.90664 3.78409 8.32983 3.96409 7.78983V5.45801H0.957273C0.347727 6.67301 0 8.04755 0 9.49983C0 10.9521 0.347727 12.3266 0.957273 13.5416L3.96409 11.2098Z"
            fill="#FBBC05"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M9 4.07955C10.3214 4.07955 11.5077 4.53364 12.4405 5.42545L15.0218 2.84409C13.4632 1.39182 11.4259 0.5 9 0.5C5.48182 0.5 2.43818 2.51682 0.957275 5.45818L3.96409 7.79C4.67182 5.66273 6.65591 4.07955 9 4.07955Z"
            fill="#EA4335"
          />
        </g>
        <defs>
          <clipPath id="clip0_1165_3770">
            <rect
              width="18"
              height="18"
              fill="white"
              transform="translate(0 0.5)"
            />
          </clipPath>
        </defs>
      </>
    ),
    viewBox: '0 0 24 24',
  },
  appleIcon: {
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11.1477 3.44363C10.4702 4.28696 9.33239 4.91945 8.42472 4.91945C8.32244 4.91945 8.22017 4.90627 8.15625 4.89309C8.14347 4.84039 8.1179 4.68226 8.1179 4.52414C8.1179 3.44363 8.64205 2.38948 9.21733 1.71746C9.94602 0.834607 11.1605 0.175761 12.1705 0.13623C12.196 0.254823 12.2088 0.399769 12.2088 0.544715C12.2088 1.61204 11.7614 2.67937 11.1477 3.44363ZM6.70181 20.4707C6.24623 20.6724 5.81449 20.8635 5.22869 20.8635C3.97585 20.8635 3.10653 19.6776 2.10938 18.2281C0.946023 16.5151 0 13.8666 0 11.363C0 7.33082 2.54403 5.19616 5.04972 5.19616C5.78105 5.19616 6.44929 5.47256 7.04349 5.71834C7.51913 5.91508 7.94732 6.09219 8.32244 6.09219C8.64782 6.09219 9.05331 5.92703 9.52593 5.73453C10.186 5.46566 10.9771 5.14345 11.8636 5.14345C12.4261 5.14345 14.4844 5.19616 15.8395 7.19905C15.8317 7.20528 15.8152 7.21655 15.791 7.23297C15.4576 7.45985 13.679 8.67007 13.679 11.1521C13.679 14.2223 16.2742 15.316 16.3636 15.3424C16.3613 15.3483 16.3561 15.3652 16.3476 15.392C16.2623 15.6642 15.8556 16.9616 14.983 18.2808C14.1264 19.5458 13.2188 20.8371 11.8636 20.8371C11.1957 20.8371 10.7702 20.6429 10.3307 20.4423C9.86181 20.2282 9.37712 20.007 8.56534 20.007C7.7492 20.007 7.21064 20.2454 6.70181 20.4707Z"
        fill="black"
      />
    ),
    viewBox: '0 0 24 24',
  },
  video: {
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5 16C3.34315 16 2 14.6569 2 13V5C2 3.34315 3.34315 2 5 2H15C16.6569 2 18 3.34315 18 5V13C18 14.6569 16.6569 16 15 16H13V17C13 17.5523 12.5523 18 12 18H8C7.44772 18 7 17.5523 7 17V16H5ZM15 4H5C4.44772 4 4 4.44772 4 5V13C4 13.5523 4.44772 14 5 14H15C15.5523 14 16 13.5523 16 13V5C16 4.44772 15.5523 4 15 4Z"
        fill="#FF5500"
      />
    ),
    viewBox: '0 0 18 18',
  },
  remove: {
    viewBox: '0 0 12 12',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.41413 6.00001L9.70713 3.70701C10.0981 3.31601 10.0981 2.68401 9.70713 2.29301C9.31613 1.90201 8.68413 1.90201 8.29313 2.29301L6.00013 4.58601L3.70713 2.29301C3.31613 1.90201 2.68413 1.90201 2.29313 2.29301C1.90213 2.68401 1.90213 3.31601 2.29313 3.70701L4.58613 6.00001L2.29313 8.29301C1.90213 8.68401 1.90213 9.31601 2.29313 9.70701C2.48813 9.90201 2.74413 10 3.00013 10C3.25613 10 3.51213 9.90201 3.70713 9.70701L6.00013 7.41401L8.29313 9.70701C8.48813 9.90201 8.74413 10 9.00013 10C9.25613 10 9.51213 9.90201 9.70713 9.70701C10.0981 9.31601 10.0981 8.68401 9.70713 8.29301L7.41413 6.00001Z"
      />
    ),
  },
  chevronLeft: {
    viewBox: '0 0 16 16',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11.2071 12.7929C11.5976 13.1834 11.5976 13.8166 11.2071 14.2071C10.8166 14.5976 10.1834 14.5976 9.79289 14.2071L4.79289 9.20711C4.40237 8.81658 4.40237 8.18342 4.79289 7.79289L9.79289 2.79289C10.1834 2.40237 10.8166 2.40237 11.2071 2.79289C11.5976 3.18342 11.5976 3.81658 11.2071 4.20711L6.91421 8.5L11.2071 12.7929Z"
      />
    ),
  },
  more: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        xmlns="http://www.w3.org/2000/svg"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6 10C6 11.1046 5.10457 12 4 12C2.89543 12 2 11.1046 2 10C2 8.89543 2.89543 8 4 8C5.10457 8 6 8.89543 6 10ZM12 10C12 11.1046 11.1046 12 10 12C8.89543 12 8 11.1046 8 10C8 8.89543 8.89543 8 10 8C11.1046 8 12 8.89543 12 10ZM18 10C18 11.1046 17.1046 12 16 12C14.8954 12 14 11.1046 14 10C14 8.89543 14.8954 8 16 8C17.1046 8 18 8.89543 18 10Z"
        fill="#FF5500"
      />
    ),
  },
  warning: {
    viewBox: '0 0 24 24',
    svg: (
      <>
        <path
          d="M10.254 4.1751C11.0173 2.80911 12.9827 2.80911 13.746 4.1751L21.6237 18.2745C22.3685 19.6076 21.4048 21.25 19.8777 21.25H4.1223C2.59519 21.25 1.63148 19.6076 2.37634 18.2745L10.254 4.1751Z"
          stroke="#876D1C"
          strokeWidth="2"
        />
        <line
          x1="12"
          y1="10"
          x2="12"
          y2="14"
          stroke="#876D1C"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <circle cx="12" cy="17" r="1" fill="#876D1C" />
      </>
    ),
  },
  chevronUp: {
    viewBox: '0 0 16 16',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3.70711 11.7071C3.31658 12.0976 2.68342 12.0976 2.29289 11.7071C1.90237 11.3166 1.90237 10.6834 2.29289 10.2929L7.29289 5.29289C7.68342 4.90237 8.31658 4.90237 8.70711 5.29289L13.7071 10.2929C14.0976 10.6834 14.0976 11.3166 13.7071 11.7071C13.3166 12.0976 12.6834 12.0976 12.2929 11.7071L8 7.41421L3.70711 11.7071Z"
      />
    ),
  },
  qrCode: {
    svg: (
      <>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M4.4999 2.40002C3.3401 2.40002 2.3999 3.34023 2.3999 4.50002V8.70002C2.3999 9.85982 3.3401 10.8 4.4999 10.8H8.6999C9.8597 10.8 10.7999 9.85982 10.7999 8.70002V4.50002C10.7999 3.34023 9.8597 2.40002 8.6999 2.40002H4.4999ZM8.9999 4.20002H4.1999V9.00002H8.9999V4.20002Z"
          fill="#FF5500"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M4.4999 13.3999C3.3401 13.3999 2.3999 14.3401 2.3999 15.4999V19.6999C2.3999 20.8597 3.3401 21.7999 4.4999 21.7999H8.6999C9.8597 21.7999 10.7999 20.8597 10.7999 19.6999V15.4999C10.7999 14.3401 9.8597 13.3999 8.6999 13.3999H4.4999ZM8.9999 15.1999H4.1999V19.9999H8.9999V15.1999Z"
          fill="#FF5500"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M15.4999 2.3999C14.3401 2.3999 13.3999 3.3401 13.3999 4.4999V8.6999C13.3999 9.8597 14.3401 10.7999 15.4999 10.7999H19.6999C20.8597 10.7999 21.7999 9.8597 21.7999 8.6999V4.4999C21.7999 3.3401 20.8597 2.3999 19.6999 2.3999H15.4999ZM19.9999 4.1999H15.1999V8.9999H19.9999V4.1999Z"
          fill="#FF5500"
        />
        <path
          d="M13.2 13.9C13.2 13.5134 13.5134 13.2 13.9 13.2H16V16H13.2V13.9Z"
          fill="#FF5500"
        />
        <path
          d="M18.8 16H16V18.8H13.2V20.9C13.2 21.2866 13.5134 21.6 13.9 21.6H16V18.8H18.8V21.6H20.9C21.2866 21.6 21.6 21.2866 21.6 20.9V18.8H18.8V16Z"
          fill="#FF5500"
        />
        <path
          d="M18.8 13.2H20.9C21.2866 13.2 21.6 13.5134 21.6 13.9V16H18.8V13.2Z"
          fill="#FF5500"
        />
      </>
    ),
    viewBox: '0 0 24 24',
  },
  chevronDown: {
    viewBox: '0 0 16 16',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12.2929 5.29289C12.6834 4.90237 13.3166 4.90237 13.7071 5.29289C14.0976 5.68342 14.0976 6.31658 13.7071 6.70711L8.70711 11.7071C8.31658 12.0976 7.68342 12.0976 7.29289 11.7071L2.29289 6.70711C1.90237 6.31658 1.90237 5.68342 2.29289 5.29289C2.68342 4.90237 3.31658 4.90237 3.70711 5.29289L8 9.58579L12.2929 5.29289Z"
      />
    ),
  },
  chevronRight: {
    viewBox: '0 0 16 16',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.79289 4.20711C4.40237 3.81658 4.40237 3.18342 4.79289 2.79289C5.18342 2.40237 5.81658 2.40237 6.20711 2.79289L11.2071 7.79289C11.5976 8.18342 11.5976 8.81658 11.2071 9.20711L6.20711 14.2071C5.81658 14.5976 5.18342 14.5976 4.79289 14.2071C4.40237 13.8166 4.40237 13.1834 4.79289 12.7929L9.08579 8.5L4.79289 4.20711Z"
      />
    ),
  },
  arrowUp: {
    viewBox: '0 0 16 16',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12.7077 6.29167L12.7909 6.38588C13.0705 6.74547 13.0705 7.25206 12.7909 7.61166L12.7077 7.70587L12.6135 7.78905C12.2538 8.06866 11.7472 8.06866 11.3876 7.78905L11.2933 7.70587L9 5.41364L9.00036 12.9986C9.00036 13.5115 8.61432 13.9341 8.11698 13.9919L8.00036 13.9986L7.8836 13.9919C7.42451 13.9386 7.06027 13.5743 7.00694 13.1153L7.00022 12.9986L7 5.41364L4.70723 7.70587L4.61302 7.78905C4.2207 8.09407 3.65342 8.06635 3.29291 7.70587C2.9324 7.34539 2.90467 6.77816 3.20972 6.38588L3.29291 6.29167L7.29311 2.29171L7.41358 2.18888L7.49613 2.13491L7.61399 2.07613L7.71267 2.04085L7.85126 2.00984L7.94648 2L8.05445 2.00028L8.14877 2.00984L8.28817 2.04094L8.41481 2.08848L8.51988 2.14419L8.60202 2.19994L8.66581 2.25236L8.70747 2.29171L12.7077 6.29167Z"
      />
    ),
  },
  arrowDown: {
    viewBox: '0 0 16 16',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3.29289 9.70696L3.20969 9.61275C2.93007 9.25315 2.93007 8.74656 3.20969 8.38697L3.29289 8.29276L3.3871 8.20957C3.74673 7.92997 4.25336 7.92997 4.61299 8.20957L4.7072 8.29276L7.00055 10.585L7.00019 2.99999C7.00019 2.48715 7.38623 2.06448 7.88357 2.00671L8.00019 1.99999L8.11695 2.00671C8.57604 2.06004 8.94028 2.42428 8.99361 2.88337L9.00033 2.99999L9.00055 10.585L11.2933 8.29276L11.3875 8.20957C11.7799 7.90455 12.3471 7.93228 12.7076 8.29276C13.0681 8.65324 13.0959 9.22046 12.7908 9.61275L12.7076 9.70696L8.70744 13.7069L8.58697 13.8097L8.50442 13.8637L8.38656 13.9225L8.28788 13.9578L8.14929 13.9888L8.05407 13.9986L7.94609 13.9983L7.85178 13.9888L7.71238 13.9577L7.58574 13.9101L7.48067 13.8544L7.39852 13.7987L7.33474 13.7463L7.29308 13.7069L3.29289 9.70696Z"
      />
    ),
  },
  inputsClear: {
    viewBox: '0 0 16 16',
    svg: (
      <path
        xmlns="http://www.w3.org/2000/svg"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9.41413 8.00001L12.7071 4.70701C13.0981 4.31601 13.0981 3.68401 12.7071 3.29301C12.3161 2.90201 11.6841 2.90201 11.2931 3.29301L8.00013 6.58601L4.70713 3.29301C4.31613 2.90201 3.68413 2.90201 3.29313 3.29301C2.90213 3.68401 2.90213 4.31601 3.29313 4.70701L6.58613 8.00001L3.29313 11.293C2.90213 11.684 2.90213 12.316 3.29313 12.707C3.48813 12.902 3.74413 13 4.00013 13C4.25613 13 4.51213 12.902 4.70713 12.707L8.00013 9.41401L11.2931 12.707C11.4881 12.902 11.7441 13 12.0001 13C12.2561 13 12.5121 12.902 12.7071 12.707C13.0981 12.316 13.0981 11.684 12.7071 11.293L9.41413 8.00001Z"
      />
    ),
  },
  actionsCopy: {
    viewbox: '0 0 24 24',
    svg: (
      <>
        <path
          stroke="currentColor"
          d="M4.5 6.75H3L3 15.75L12 15.75V14.25"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          stroke="currentColor"
          d="M6.82269 3L15.75 3L15.75 12L6.75 12L6.82269 3Z"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  actionsEdit: {
    viewBox: '0 0 16 16',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2 13V10C2 9.73478 2.10536 9.48043 2.29289 9.29289L9.29289 2.29289C9.68342 1.90237 10.3166 1.90237 10.7071 2.29289L13.7071 5.29289C14.0976 5.68342 14.0976 6.31658 13.7071 6.70711L6.70711 13.7071C6.51957 13.8946 6.26522 14 6 14H3C2.44772 14 2 13.5523 2 13ZM10 4.41421L4 10.4142V12H5.58579L11.5858 6L10 4.41421Z"
      />
    ),
  },
  inputsClose: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11.4141 10L15.7071 5.70701C16.0981 5.31601 16.0981 4.68401 15.7071 4.29301C15.3161 3.90201 14.6841 3.90201 14.2931 4.29301L10.0001 8.58601L5.70713 4.29301C5.31613 3.90201 4.68413 3.90201 4.29313 4.29301C3.90213 4.68401 3.90213 5.31601 4.29313 5.70701L8.58613 10L4.29313 14.293C3.90213 14.684 3.90213 15.316 4.29313 15.707C4.48813 15.902 4.74413 16 5.00013 16C5.25613 16 5.51213 15.902 5.70713 15.707L10.0001 11.414L14.2931 15.707C14.4881 15.902 14.7441 16 15.0001 16C15.2561 16 15.5121 15.902 15.7071 15.707C16.0981 15.316 16.0981 14.684 15.7071 14.293L11.4141 10Z"
      />
    ),
  },
  actionsSearch: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9 2C12.866 2 16 5.13401 16 9C16 10.5764 15.4789 12.0311 14.5996 13.2012L14.6545 13.2441L14.7071 13.2929L17.7071 16.2929C18.0976 16.6834 18.0976 17.3166 17.7071 17.7071C17.3466 18.0676 16.7794 18.0953 16.3871 17.7903L16.2929 17.7071L13.2929 14.7071C13.2587 14.6729 13.2275 14.6368 13.1993 14.5992C12.031 15.4789 10.5764 16 9 16C5.13401 16 2 12.866 2 9C2 5.13401 5.13401 2 9 2ZM9 4C6.23858 4 4 6.23858 4 9C4 11.7614 6.23858 14 9 14C11.7614 14 14 11.7614 14 9C14 6.23858 11.7614 4 9 4Z"
      />
    ),
  },
  actionsHelp: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.0001 1.66669C14.6025 1.66669 18.3334 5.39765 18.3334 10C18.3334 14.6024 14.6025 18.3334 10.0001 18.3334C5.39771 18.3334 1.66675 14.6024 1.66675 10C1.66675 5.39765 5.39771 1.66669 10.0001 1.66669ZM10.0001 3.33335C6.31818 3.33335 3.33341 6.31812 3.33341 10C3.33341 13.6819 6.31818 16.6667 10.0001 16.6667C13.682 16.6667 16.6667 13.6819 16.6667 10C16.6667 6.31812 13.682 3.33335 10.0001 3.33335ZM10.0001 13.3334C10.4603 13.3334 10.8334 13.7064 10.8334 14.1667C10.8334 14.6269 10.4603 15 10.0001 15C9.53984 15 9.16675 14.6269 9.16675 14.1667C9.16675 13.7064 9.53984 13.3334 10.0001 13.3334ZM10.0001 5.00002C11.841 5.00002 13.3334 6.4924 13.3334 8.33335C13.3334 9.82903 12.3483 11.0946 10.9916 11.5167L10.8334 11.5617V11.6667C10.8334 12.1269 10.4603 12.5 10.0001 12.5C9.57272 12.5 9.22049 12.1783 9.17235 11.7639L9.16675 11.6667V10.8334C9.16675 10.406 9.48845 10.0538 9.9029 10.0056L10.1245 9.99545C10.9869 9.93182 11.6667 9.21199 11.6667 8.33335C11.6667 7.41288 10.9206 6.66669 10.0001 6.66669C9.07961 6.66669 8.33342 7.41288 8.33342 8.33335C8.33342 8.79359 7.96032 9.16669 7.50008 9.16669C7.03984 9.16669 6.66675 8.79359 6.66675 8.33335C6.66675 6.4924 8.15913 5.00002 10.0001 5.00002Z"
      />
    ),
  },
  actionsSend: {
    viewBox: '0 0 25 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M20.7259 3.14098L3.10278 9.48531C2.84646 9.56954 2.62481 9.75662 2.50437 10.0216C2.29404 10.4843 2.46557 11.0233 2.88615 11.2844C2.92773 11.3103 2.97115 11.3331 3.01601 11.3526L10.4379 14.7262L13.8115 22.1482C13.8311 22.193 13.8539 22.2364 13.8797 22.278C14.1408 22.6986 14.6798 22.8701 15.1426 22.6598C15.4075 22.5393 15.5946 22.3177 15.6788 22.0614L22.0232 4.43825C22.1092 4.22012 22.1193 3.97099 22.0336 3.73303C21.982 3.58963 21.901 3.46528 21.7999 3.36431C21.6989 3.26315 21.5745 3.18215 21.4311 3.13053C21.1932 3.04486 20.9441 3.05496 20.7259 3.14098ZM14.6209 19.0955L19.4316 5.7326L6.06861 10.5433L10.976 12.7739L14.0215 9.72844C14.412 9.33792 15.0452 9.33792 15.4357 9.72844C15.8262 10.119 15.8262 10.7521 15.4357 11.1427L12.3902 14.1881L14.6209 19.0955Z"
      />
    ),
  },
  actionsAttach: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M17.7781 9.12139L17.778 9.12135C18.9496 7.94978 18.9496 6.05028 17.778 4.87871C16.6098 3.71051 14.7179 3.70715 13.5455 4.86864L13.5355 4.87875L6.46446 11.9498C6.46228 11.952 6.46008 11.9542 6.45788 11.9563C4.51184 13.9095 4.51404 17.0704 6.46447 19.0208C8.41267 20.969 11.5686 20.9734 13.5223 19.034C13.5265 19.0296 13.5308 19.0252 13.5352 19.0209L19.8991 12.6569C20.2897 12.2664 20.9228 12.2664 21.3134 12.6569C21.7039 13.0474 21.7039 13.6806 21.3134 14.0711L14.9496 20.4349L14.9497 20.435C12.2161 23.1687 7.78392 23.1687 5.05025 20.435C2.31658 17.7014 2.31658 13.2692 5.05025 10.5355L5.05028 10.5356L12.1213 3.46459L12.1212 3.4645C14.0738 1.51188 17.2396 1.51188 19.1922 3.4645C21.1421 5.41437 21.1448 8.57403 19.2005 10.5273L19.1923 10.5356L12.1212 17.6067L12.1067 17.6209C10.934 18.7782 9.04519 18.7735 7.87838 17.6067C6.7068 16.4351 6.70681 14.5356 7.87838 13.364L7.87847 13.3641L7.8786 13.364L12.8283 8.41424C13.2189 8.02371 13.852 8.02371 14.2426 8.41424C14.6331 8.80476 14.6331 9.43793 14.2426 9.82845L9.29281 14.7782L9.2845 14.7864C8.90209 15.1776 8.90478 15.8046 9.29259 16.1924C9.68312 16.583 10.3163 16.583 10.7068 16.1924L10.7069 16.1925L10.707 16.1925L17.7781 9.12139Z"
      />
    ),
  },
  resolved: {
    viewBox: '0 0 24 24',
    svg: (
      <>
        <circle
          cx="12"
          cy="12"
          r="9"
          strokeWidth="2"
          stroke="currentColor"
          fill="transparent"
        />
        <path d="M15.5309 8.1827C15.0504 7.84819 14.4029 7.98636 14.0826 8.48812L11.012 13.3312L9.68213 12.255C9.22955 11.8841 8.56808 11.9714 8.21994 12.4513C7.86484 12.9313 7.9484 13.6148 8.40794 13.9784L10.6291 15.7673C10.8031 15.92 11.0259 16 11.2627 16C11.3184 16 11.3741 15.9927 11.4298 15.9855C11.7153 15.9346 11.9729 15.7673 12.133 15.5128L15.8303 9.69526C16.1436 9.1935 16.0113 8.51721 15.5309 8.1827Z" />
      </>
    ),
  },
  sync: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.48426 9.00012C5.99704 8.99205 6.42573 9.37139 6.49132 9.86776L6.49988 9.98426L6.50221 10.1404C6.57041 12.3036 8.20469 14 10.1661 14C10.4448 14 10.7187 13.9662 10.9846 13.9L10.7929 13.7071C10.4024 13.3166 10.4024 12.6834 10.7929 12.2929C11.1834 11.9024 11.8166 11.9024 12.2071 12.2929L14.2071 14.2929C14.5976 14.6834 14.5976 15.3166 14.2071 15.7071L12.2071 17.7071C11.8166 18.0976 11.1834 18.0976 10.7929 17.7071C10.4024 17.3166 10.4024 16.6834 10.7929 16.2929L11.1823 15.9037C10.8493 15.9675 10.5096 16 10.1661 16C7.1662 16 4.72335 13.5384 4.51413 10.4172L4.50283 10.1876L4.50013 10.0157C4.49143 9.46352 4.93205 9.00882 5.48426 9.00012ZM10.2071 2.29289C10.5676 2.65338 10.5953 3.22061 10.2903 3.6129L10.2071 3.70711L9.81759 4.09625C10.1504 4.03244 10.49 4 10.8334 4C13.979 4 16.5 6.70112 16.5 10C16.5 10.5523 16.0523 11 15.5 11C14.9477 11 14.5 10.5523 14.5 10C14.5 7.77603 12.8424 6 10.8334 6C10.5548 6 10.281 6.03374 10.0151 6.09988L10.2071 6.29289C10.5976 6.68342 10.5976 7.31658 10.2071 7.70711C9.84663 8.06759 9.27939 8.09532 8.8871 7.7903L8.7929 7.70711L6.7929 5.70711C6.43241 5.34662 6.40468 4.77939 6.70971 4.3871L6.7929 4.29289L8.7929 2.29289C9.18342 1.90237 9.81659 1.90237 10.2071 2.29289Z"
      />
    ),
  },
  starRating: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9.55472 15.9827L6.09222 17.885C5.62452 18.1419 5.04604 17.954 4.80016 17.4652C4.70225 17.2706 4.66846 17.0477 4.70403 16.831L5.36531 12.8019C5.41854 12.4775 5.31564 12.1466 5.09015 11.9169L2.28893 9.06349C1.91055 8.67806 1.90281 8.04507 2.27163 7.64966C2.4185 7.49221 2.61095 7.38974 2.81917 7.35812L6.69037 6.77028C7.00199 6.72296 7.27137 6.51843 7.41074 6.22335L9.14199 2.55755C9.37584 2.06239 9.94953 1.85909 10.4234 2.10347C10.612 2.20078 10.7648 2.36038 10.8579 2.55755L12.5891 6.22335C12.7285 6.51843 12.9979 6.72296 13.3095 6.77028L17.1807 7.35812C17.7036 7.43752 18.0659 7.94487 17.9899 8.49131C17.9597 8.70891 17.8616 8.91001 17.7109 9.06349L14.9097 11.9169C14.6842 12.1466 14.5813 12.4775 14.6346 12.8019L15.2958 16.831C15.3852 17.3752 15.0354 17.8921 14.5146 17.9854C14.3072 18.0226 14.0939 17.9873 13.9077 17.885L10.4452 15.9827C10.1664 15.8295 9.83345 15.8295 9.55472 15.9827ZM7.01559 15.0957L8.5917 14.2298C9.47016 13.7472 10.5297 13.7472 11.4082 14.2298L12.9843 15.0957L12.661 13.1258C12.5051 12.1761 12.8075 11.2034 13.4825 10.5158L14.9318 9.03955L13.0093 8.74762C12.0323 8.59926 11.2015 7.96851 10.7807 7.07743L9.99994 5.42428L9.2192 7.07743C8.79837 7.96851 7.96761 8.59926 6.99062 8.74762L5.0681 9.03955L6.51736 10.5158C7.19234 11.2034 7.49478 12.1761 7.3389 13.1258L7.01559 15.0957Z"
      />
    ),
  },
  percentage: {
    viewBox: '0 0 20 20',
    svg: (
      <>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M5.5 7C6.32843 7 7 6.32843 7 5.5C7 4.67157 6.32843 4 5.5 4C4.67157 4 4 4.67157 4 5.5C4 6.32843 4.67157 7 5.5 7ZM5.5 9C7.433 9 9 7.433 9 5.5C9 3.567 7.433 2 5.5 2C3.567 2 2 3.567 2 5.5C2 7.433 3.567 9 5.5 9Z"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M14.5 16C15.3284 16 16 15.3284 16 14.5C16 13.6716 15.3284 13 14.5 13C13.6716 13 13 13.6716 13 14.5C13 15.3284 13.6716 16 14.5 16ZM14.5 18C16.433 18 18 16.433 18 14.5C18 12.567 16.433 11 14.5 11C12.567 11 11 12.567 11 14.5C11 16.433 12.567 18 14.5 18Z"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M16.4999 3.5C16.8905 3.89053 16.8905 4.52369 16.4999 4.91422L5.00006 16.5001C4.60954 16.8906 3.97637 16.8906 3.58585 16.5001C3.19532 16.1095 3.19532 15.4764 3.58585 15.0858L15.0857 3.5C15.4763 3.10948 16.1094 3.10948 16.4999 3.5Z"
        />
      </>
    ),
  },
  priceTag: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.83419 2C9.46529 2 10.0705 2.25071 10.5168 2.69696L17.303 9.48319C18.2323 10.4125 18.2323 11.9191 17.303 12.8484L12.8484 17.303C11.9191 18.2323 10.4125 18.2323 9.48319 17.303L2.69696 10.5168C2.25071 10.0705 2 9.46529 2 8.83419V4.37959C2 3.06538 3.06538 2 4.37959 2H8.83419ZM8.83419 4H4.37959C4.16995 4 4 4.16995 4 4.37959V8.83419C4 8.93486 4.03999 9.03141 4.11118 9.10259L10.8974 15.8888C11.0456 16.0371 11.286 16.0371 11.4342 15.8888L15.8888 11.4342C16.0371 11.286 16.0371 11.0456 15.8888 10.8974L9.10259 4.11118C9.03141 4.03999 8.93486 4 8.83419 4ZM7.5 6C8.32843 6 9 6.67157 9 7.5C9 8.32843 8.32843 9 7.5 9C6.67157 9 6 8.32843 6 7.5C6 6.67157 6.67157 6 7.5 6Z"
        fill="#363940"
      />
    ),
  },
  mActionsInfo: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 0C15.5228 0 20 4.47715 20 10C20 15.5228 15.5228 20 10 20C4.47715 20 0 15.5228 0 10C0 4.47715 4.47715 0 10 0ZM10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2ZM10 8C10.5523 8 11 8.39175 11 8.875V14.125C11 14.6082 10.5523 15 10 15C9.44771 15 9 14.6082 9 14.125V8.875C9 8.39175 9.44771 8 10 8ZM10 5C10.5523 5 11 5.44772 11 6C11 6.55228 10.5523 7 10 7C9.44771 7 9 6.55228 9 6C9 5.44772 9.44771 5 10 5Z"
      />
    ),
  },
  otherPastSearch: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 2C14.4183 2 18 5.58172 18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2ZM10 4C6.68629 4 4 6.68629 4 10C4 13.3137 6.68629 16 10 16C13.3137 16 16 13.3137 16 10C16 6.68629 13.3137 4 10 4ZM10 6C10.5128 6 10.9355 6.38604 10.9933 6.88338L11 7V9.58579L12.7071 11.2929C13.0976 11.6834 13.0976 12.3166 12.7071 12.7071C12.3466 13.0676 11.7794 13.0953 11.3871 12.7903L11.2929 12.7071L9.29289 10.7071C9.13661 10.5508 9.0374 10.3481 9.00867 10.1314L9 10V7C9 6.44772 9.44772 6 10 6Z"
      />
    ),
  },
  sExternal: {
    viewBox: '0 0 16 16',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5 2C5.55228 2 6 2.44772 6 3C6 3.51284 5.61396 3.93551 5.11662 3.99327L5 4C4.48716 4 4.06449 4.38604 4.00673 4.88338L4 5V11C4 11.5128 4.38604 11.9355 4.88338 11.9933L5 12H11C11.5128 12 11.9355 11.614 11.9933 11.1166L12 11C12 10.4477 12.4477 10 13 10C13.5523 10 14 10.4477 14 11C14 12.5977 12.7511 13.9037 11.1763 13.9949L11 14H5C3.40232 14 2.09634 12.7511 2.00509 11.1763L2 11V5C2 3.34315 3.34315 2 5 2ZM13 2L13.081 2.003L13.2007 2.02024L13.3121 2.04974L13.4232 2.09367L13.5207 2.146L13.6168 2.21279L13.7071 2.29289L13.8037 2.40469L13.8753 2.51594L13.9063 2.5769L13.9401 2.65835L13.9642 2.73401L13.9931 2.8819L14 3V7C14 7.55228 13.5523 8 13 8C12.4477 8 12 7.55228 12 7V5.414L9.70711 7.70711C9.34662 8.06759 8.77939 8.09532 8.3871 7.7903L8.29289 7.70711C7.93241 7.34662 7.90468 6.77939 8.2097 6.3871L8.29289 6.29289L10.584 4H9C8.44772 4 8 3.55228 8 3C8 2.44772 8.44772 2 9 2H13Z"
      />
    ),
  },
  sCheck: {
    viewBox: '0 0 16 16',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.91006 10.4959L3.7071 8.29291C3.31658 7.90239 2.68342 7.90239 2.29289 8.29291C1.90237 8.68343 1.90237 9.3166 2.29289 9.70712L5.29288 12.7071C5.7168 13.131 6.4159 13.0892 6.7863 12.6178L13.7863 4.61786C14.1275 4.18359 14.0521 3.55494 13.6178 3.21372C13.1835 2.87251 12.5549 2.94795 12.2136 3.38222L5.91006 10.4959Z"
      />
    ),
  },
  sPencil: {
    viewBox: '0 0 16 16',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2 13V10C2 9.73478 2.10536 9.48043 2.29289 9.29289L9.29289 2.29289C9.68342 1.90237 10.3166 1.90237 10.7071 2.29289L13.7071 5.29289C14.0976 5.68342 14.0976 6.31658 13.7071 6.70711L6.70711 13.7071C6.51957 13.8946 6.26522 14 6 14H3C2.44772 14 2 13.5523 2 13ZM10 4.41421L4 10.4142V12H5.58579L11.5858 6L10 4.41421Z"
      />
    ),
  },
  sPlus: {
    viewBox: '0 0 16 16',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.45672 8.45685L12.1822 8.45685C12.6246 8.45685 12.9821 8.09935 12.9821 7.657C12.9821 7.21464 12.6246 6.85714 12.1822 6.85714L8.45672 6.85714L8.45672 3.13163C8.45672 2.68927 8.09922 2.33177 7.65686 2.33177C7.21451 2.33177 6.857 2.68927 6.857 3.13163L6.857 6.85714L3.13149 6.85714C2.68914 6.85714 2.33163 7.21464 2.33163 7.657C2.33163 7.87761 2.42101 8.07785 2.56582 8.22267C2.71063 8.36748 2.91088 8.45685 3.13149 8.45685L6.857 8.45685L6.857 12.1824C6.857 12.403 6.94638 12.6032 7.09119 12.748C7.236 12.8928 7.43625 12.9822 7.65686 12.9822C8.09922 12.9822 8.45672 12.6247 8.45672 12.1824L8.45672 8.45685Z"
      />
    ),
  },
  mLock: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        stroke="#737373"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5.25 8.16667H3.375C2.87772 8.16667 2.40081 8.34226 2.04917 8.65482C1.69754 8.96738 1.5 9.39131 1.5 9.83333V14.8333C1.5 15.2754 1.69754 15.6993 2.04917 16.0118C2.40081 16.3244 2.87772 16.5 3.375 16.5H14.625C15.1223 16.5 15.5992 16.3244 15.9508 16.0118C16.3025 15.6993 16.5 15.2754 16.5 14.8333V9.83333C16.5 9.39131 16.3025 8.96738 15.9508 8.65482C15.5992 8.34226 15.1223 8.16667 14.625 8.16667H12.75M5.25 8.16667V4.83333C5.25 3.94928 5.64509 3.10143 6.34835 2.47631C7.05161 1.85119 8.00544 1.5 9 1.5V1.5C9.99456 1.5 10.9484 1.85119 11.6517 2.47631C12.3549 3.10143 12.75 3.94928 12.75 4.83333V8.16667M5.25 8.16667H12.75M9 11.5V13.1667"
      />
    ),
  },
  mClock: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 2C14.4183 2 18 5.58172 18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2ZM10 4C6.68629 4 4 6.68629 4 10C4 13.3137 6.68629 16 10 16C13.3137 16 16 13.3137 16 10C16 6.68629 13.3137 4 10 4ZM10 6C10.5128 6 10.9355 6.38604 10.9933 6.88338L11 7V9.58579L12.7071 11.2929C13.0976 11.6834 13.0976 12.3166 12.7071 12.7071C12.3466 13.0676 11.7794 13.0953 11.3871 12.7903L11.2929 12.7071L9.29289 10.7071C9.13661 10.5508 9.0374 10.3481 9.00867 10.1314L9 10V7C9 6.44772 9.44772 6 10 6Z"
      />
    ),
  },
  mCalendarDays: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14 2.00009C14.5523 2.00009 15 2.44781 15 3.00009V4.00009C16.6569 4.00009 18 5.34324 18 7.00009V15.0001C18 16.6569 16.6569 18.0001 15 18.0001H5C3.34315 18.0001 2 16.6569 2 15.0001V7.00009C2 5.34324 3.34315 4.00009 5 4.00009V3.00009C5 2.44781 5.44772 2.00009 6 2.00009C6.55228 2.00009 7 2.44781 7 3.00009V4.00009H13V3.00009C13 2.44781 13.4477 2.00009 14 2.00009ZM16 10.0001H4V15.0001C4 15.5524 4.44772 16.0001 5 16.0001H15C15.5523 16.0001 16 15.5524 16 15.0001V10.0001ZM14 11.0001C14.5523 11.0001 15 11.4478 15 12.0001V14.0001C15 14.5524 14.5523 15.0001 14 15.0001H12C11.4477 15.0001 11 14.5524 11 14.0001V12.0001C11 11.4478 11.4477 11.0001 12 11.0001H14ZM15 6.00009H5C4.44772 6.00009 4 6.44781 4 7.00009V8.00009H16V7.00009C16 6.44781 15.5523 6.00009 15 6.00009Z"
      />
    ),
  },
  mDebit: {
    viewBox: '0 0 20 20',
    svg: (
      <>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M15 4.00568C16.6569 4.00568 18 5.34882 18 7.00568V13.0057C18 14.6625 16.6569 16.0057 15 16.0057H5C3.34315 16.0057 2 14.6625 2 13.0057V7.00568C2 5.34882 3.34315 4.00568 5 4.00568H15ZM4 10.0057V13.0057C4 13.558 4.44772 14.0057 5 14.0057H15C15.5523 14.0057 16 13.558 16 13.0057V10.0057H4ZM4 10.0057H16V7.00568C16 6.45339 15.5523 6.00568 15 6.00568H5C4.44772 6.00568 4 6.45339 4 7.00568V10.0057Z"
        />
        <path d="M9.93632 12.1017C9.58432 12.1017 9.24432 12.0617 8.91632 11.9817C8.58832 11.9017 8.31832 11.7977 8.10632 11.6697L8.56232 10.6377C8.76232 10.7537 8.98432 10.8477 9.22832 10.9197C9.47632 10.9877 9.71632 11.0217 9.94832 11.0217C10.0843 11.0217 10.1903 11.0137 10.2663 10.9977C10.3463 10.9777 10.4043 10.9517 10.4403 10.9197C10.4763 10.8837 10.4943 10.8417 10.4943 10.7937C10.4943 10.7177 10.4523 10.6577 10.3683 10.6137C10.2843 10.5697 10.1723 10.5337 10.0323 10.5057C9.89632 10.4737 9.74632 10.4417 9.58232 10.4097C9.41832 10.3737 9.25232 10.3277 9.08432 10.2717C8.92032 10.2157 8.76832 10.1417 8.62832 10.0497C8.49232 9.95768 8.38232 9.83768 8.29832 9.68968C8.21432 9.53768 8.17232 9.34968 8.17232 9.12568C8.17232 8.86568 8.24432 8.62968 8.38832 8.41768C8.53632 8.20168 8.75432 8.02968 9.04232 7.90168C9.33432 7.77368 9.69632 7.70968 10.1283 7.70968C10.4123 7.70968 10.6923 7.73968 10.9683 7.79968C11.2443 7.85968 11.4923 7.95168 11.7123 8.07568L11.2863 9.10168C11.0783 8.99768 10.8763 8.91968 10.6803 8.86768C10.4883 8.81568 10.3003 8.78968 10.1163 8.78968C9.98032 8.78968 9.87232 8.80168 9.79232 8.82568C9.71232 8.84968 9.65432 8.88168 9.61832 8.92168C9.58632 8.96168 9.57032 9.00568 9.57032 9.05368C9.57032 9.12568 9.61232 9.18368 9.69632 9.22768C9.78032 9.26768 9.89032 9.30168 10.0263 9.32968C10.1663 9.35768 10.3183 9.38768 10.4823 9.41968C10.6503 9.45168 10.8163 9.49568 10.9803 9.55168C11.1443 9.60768 11.2943 9.68168 11.4303 9.77368C11.5703 9.86568 11.6823 9.98568 11.7663 10.1337C11.8503 10.2817 11.8923 10.4657 11.8923 10.6857C11.8923 10.9417 11.8183 11.1777 11.6703 11.3937C11.5263 11.6057 11.3103 11.7777 11.0223 11.9097C10.7343 12.0377 10.3723 12.1017 9.93632 12.1017ZM9.64832 12.7257V11.9457H10.4943V12.7257H9.64832ZM9.64832 7.86568V7.08568H10.4943V7.86568H9.64832Z" />
      </>
    ),
  },
  mCard: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M15 4.00015C16.6569 4.00015 18 5.3433 18 7.00015V13.0002C18 14.657 16.6569 16.0002 15 16.0002H5C3.34315 16.0002 2 14.657 2 13.0002V7.00015C2 5.3433 3.34315 4.00015 5 4.00015H15ZM4 10.0002V13.0002C4 13.5524 4.44772 14.0002 5 14.0002H15C15.5523 14.0002 16 13.5524 16 13.0002V10.0002H4ZM4 8.00015H16V7.00015C16 6.44787 15.5523 6.00015 15 6.00015H5C4.44772 6.00015 4 6.44787 4 7.00015V8.00015Z"
      />
    ),
  },
  mPhone: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.31623 8.94874C8.72457 8.81263 9 8.43049 9 8.00006V5.00006C9 3.34321 7.65685 2.00006 6 2.00006H3C2.44772 2.00006 2 2.44778 2 3.00006C2 10.8548 9.14528 18.0001 17 18.0001C17.5523 18.0001 18 17.5523 18 17.0001V14.0001C18 12.3432 16.6569 11.0001 15 11.0001H12C11.5696 11.0001 11.1874 11.2755 11.0513 11.6838L10.3043 13.9249C8.74458 12.9125 7.37501 11.582 6.31438 10.0523L6.07516 9.69577L8.31623 8.94874ZM12.7208 13.0001H15C15.5523 13.0001 16 13.4478 16 14.0001V15.9546C14.7978 15.8457 13.621 15.544 12.503 15.0828L12.0866 14.9025L12.7208 13.0001ZM4.04542 4.00006H6C6.55228 4.00006 7 4.44778 7 5.00006V7.2793L5.09757 7.91345C4.53454 6.67284 4.16796 5.35252 4.04542 4.00006Z"
      />
    ),
  },
  mLink: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3.47186 9.42136L4.31457 8.57864C4.70707 8.18615 5.34343 8.18615 5.73593 8.57864C6.10039 8.9431 6.12643 9.51784 5.81403 9.91234L5.73593 10L4.89322 10.8427C3.71573 12.0202 3.71573 13.9293 4.89322 15.1068C6.02866 16.2422 7.84437 16.2828 9.0284 15.2284L9.15729 15.1068L10 14.2641C10.3925 13.8716 11.0289 13.8716 11.4214 14.2641C11.7858 14.6285 11.8119 15.2033 11.4995 15.5978L11.4214 15.6854L10.5786 16.5281C8.61616 18.4906 5.43435 18.4906 3.47186 16.5281C1.56102 14.6173 1.51074 11.5505 3.32101 9.57878L3.47186 9.42136ZM9.42136 3.47186C11.3838 1.50938 14.5657 1.50938 16.5281 3.47186C18.4906 5.43435 18.4906 8.61616 16.5281 10.5786L15.6854 11.4214C15.2929 11.8139 14.6566 11.8139 14.2641 11.4214C13.8716 11.0289 13.8716 10.3925 14.2641 10L15.1068 9.15729C16.2843 7.9798 16.2843 6.07071 15.1068 4.89322C13.9293 3.71573 12.0202 3.71573 10.8427 4.89322L10 5.73593C9.6075 6.12843 8.97114 6.12843 8.57864 5.73593C8.18615 5.34343 8.18615 4.70707 8.57864 4.31457L9.42136 3.47186ZM10.4212 8.15729C10.8137 7.76479 11.4501 7.76479 11.8426 8.15729C12.2351 8.54978 12.2351 9.18615 11.8426 9.57864L9.57852 11.8427C9.18603 12.2352 8.54966 12.2352 8.15717 11.8427C7.76467 11.4502 7.76467 10.8139 8.15717 10.4214L10.4212 8.15729Z"
      />
    ),
  },
  mActionsCopy: {
    viewbox: '0 0 20 20',
    svg: (
      <>
        <path
          stroke="currentColor"
          style={{
            fill: 'transparent',
          }}
          d="M4.5 6.75H3L3 15.75L12 15.75V14.25"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          stroke="currentColor"
          style={{
            fill: 'transparent',
          }}
          d="M6.82269 3L15.75 3L15.75 12L6.75 12L6.82269 3Z"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  mActionsEmail: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M15 4C16.6569 4 18 5.34315 18 7V13C18 14.6569 16.6569 16 15 16H5C3.34315 16 2 14.6569 2 13V7C2 5.34315 3.34315 4 5 4H15ZM16 7.3L10.6585 11.7526C10.3129 12.055 9.81106 12.0802 9.43941 11.8282L9.3415 11.7526L4 7.301V13C4 13.5523 4.44772 14 5 14H15C15.5523 14 16 13.5523 16 13V7.3ZM14.4319 6H5.56689L9.99989 9.67123L14.4319 6Z"
      />
    ),
  },
  mShopFront: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.74998 4H15.25C15.25 4 16 3.84375 16 3C16 2.15625 15.25 2 15.25 2H4.74998C4.74998 2 3.99998 2.17188 3.99998 3C3.99998 3.82812 4.74998 4 4.74998 4ZM3.57089 18H16.4288C16.6688 18 16.857 17.7598 16.857 17.4533V13.2C16.857 13.2 16.6681 12.4 15.9062 12.4C15.1443 12.4 14.9999 13.2 14.9999 13.2V14.8V16H9.99991H4.99991V14.8V14V13.2C4.99991 13.2 4.83101 12.4 4.07803 12.4C3.31613 12.4 3.14273 13.2 3.14273 13.2V17.4533C3.14273 17.7515 3.33086 18 3.57089 18ZM2.00057 9.19282C2.00267 9.30012 2.01573 9.40589 2.03984 9.50592C2.31148 10.6126 3.41719 11.5 4.58922 11.5L4.66796 11.4995H4.73305C5.47937 11.4637 6.02623 11.0078 6.40792 10.6041C6.90698 11.0597 7.60155 11.3987 8.25583 11.3987C8.80078 11.3987 9.15847 11.1981 9.58136 10.8856C9.67901 10.8111 9.82228 10.6612 9.94038 10.5377C9.95261 10.5249 9.96457 10.5124 9.97619 10.5003H10.0068L10.0307 10.5094C10.0829 10.5635 10.1419 10.6414 10.2013 10.7197C10.2833 10.8277 10.3659 10.9366 10.432 10.9852C10.855 11.2976 11.3685 11.4426 11.8927 11.3987C12.5033 11.35 13.1099 11.0576 13.6099 10.5995C13.9876 10.9983 14.5338 11.4637 15.2863 11.4995C16.5193 11.558 17.6909 10.8615 17.9706 9.70807C18.0069 9.55749 18.0154 9.36436 18 9.1669C17.9999 9.09389 17.9912 9.02262 17.9741 8.95276C17.9366 8.72479 17.8685 8.50974 17.7761 8.36812C17.6429 8.11441 16.7981 6.39314 16.2369 5.23454C16.0922 4.93504 15.7849 4.8 15.5102 4.8H4.49073C4.21598 4.8 3.90874 4.93504 3.76397 5.23454C3.20662 6.38002 2.36719 8.09057 2.22527 8.36687C2.08262 8.57878 2.00724 8.8532 2.00073 9.12364C1.99981 9.14653 1.99976 9.16959 2.00057 9.19282ZM3.79819 5.25354L3.76366 5.23518C3.76376 5.23496 3.76387 5.23475 3.76397 5.23454C3.76407 5.23432 3.76418 5.23411 3.76428 5.23389L3.79874 5.25238C3.79856 5.25276 3.79837 5.25315 3.79819 5.25354ZM2.96721 8.85778C2.96728 8.85765 2.96736 8.85753 2.96744 8.85741C3.0232 8.86654 3.10724 8.8804 3.2339 8.90144L2.96721 8.85778ZM3.60383 9.07379C3.60383 9.07379 3.60333 9.06494 3.60369 9.04616C3.72721 8.85613 3.8778 8.57733 4.07948 8.18002C4.15916 8.02306 4.2153 7.88248 4.27153 7.74168C4.40604 7.40488 4.54106 7.06677 4.99998 6.49995H14.982C15.3035 6.95456 15.6318 7.60327 15.8572 8.04867C15.8797 8.09314 15.9012 8.13559 15.9215 8.17561C16.1231 8.57225 16.2737 8.85092 16.3972 9.04089C16.3975 9.05511 16.3973 9.06364 16.3971 9.06695C16.3971 9.068 16.3971 9.06853 16.3971 9.06853L16.3976 9.06695C16.3153 9.39659 15.9203 9.7131 15.3536 9.69168C15.2958 9.68902 15.186 9.64407 15.0196 9.50054C14.8756 9.37645 14.7354 9.22027 14.5976 9.06402C14.5859 9.05066 14.5741 9.03729 14.5624 9.02395L14.5623 9.02393L14.5585 9.01954L14.555 9.01566C14.4945 8.9487 14.404 8.84938 14.2664 8.75812L14.2649 8.75712C13.8294 8.47113 13.243 8.49275 12.8425 8.84075L12.8382 8.84445L12.8341 8.84829C12.7672 8.90998 12.7122 8.97305 12.6696 9.02197L12.6588 9.03435L12.6332 9.06083L12.6146 9.08011L12.6097 9.08532C12.306 9.41193 11.9749 9.58054 11.7441 9.59763C11.6269 9.60648 11.5242 9.57692 11.4235 9.50399C11.4235 9.50395 11.4234 9.50389 11.4233 9.50382C11.4231 9.50354 11.4225 9.50304 11.4215 9.50213C11.4155 9.49662 11.398 9.48076 11.3663 9.44954C11.328 9.41193 11.2804 9.36346 11.2286 9.30962C11.1494 9.22726 11.064 9.136 10.9929 9.05817C10.9714 9.03465 10.9512 9.01236 10.9329 8.99192C10.624 8.64157 10.2601 8.55409 10.0068 8.55409H9.98197L9.97619 8.55428C9.73959 8.56183 9.38567 8.64063 9.07038 8.98933L9.06913 8.99072C9.05136 9.01056 9.03181 9.03216 9.011 9.05494C8.93927 9.13348 8.85259 9.22612 8.7723 9.30962C8.72053 9.36346 8.67292 9.41193 8.63465 9.44954C8.60291 9.48073 8.58547 9.49659 8.57939 9.50212L8.57937 9.50213C8.57793 9.50345 8.57748 9.50391 8.57735 9.50406C8.46726 9.58459 8.37355 9.60912 8.2658 9.59829L8.25581 9.59756C8.01969 9.58038 7.69242 9.41315 7.39862 9.09341L7.36493 9.05349C7.36461 9.05308 7.3643 9.05267 7.36399 9.05227C7.31139 8.98452 7.24388 8.90684 7.16136 8.83782C6.75676 8.48914 6.17763 8.46442 5.73968 8.75887C5.60509 8.8484 5.5093 8.95028 5.44019 9.03256L5.4254 9.04912L5.40777 9.06885C5.26749 9.22596 5.12849 9.38164 4.98371 9.50648C4.81851 9.64892 4.70841 9.69441 4.64708 9.69695L4.64556 9.69702C4.07643 9.72301 3.68465 9.40341 3.60279 9.07009L3.60383 9.07379ZM8.58118 9.50125C8.5799 9.50219 8.57863 9.50313 8.57735 9.50406C8.57727 9.50417 8.57732 9.50414 8.57732 9.50414L8.80076 9.82812L8.58118 9.50125ZM11.4236 9.50414C11.4236 9.50414 11.4237 9.50418 11.4235 9.50399C11.422 9.50293 11.4205 9.50185 11.4191 9.50076L11.2002 9.82812L11.4236 9.50414Z"
      />
    ),
  },
  mLocation: {
    viewBox: '0 0 20 20',
    fill: 'transparent',
    svg: (
      <>
        <path
          d="M14.7141 13.881L11.1783 17.4168C11.0236 17.5716 10.84 17.6944 10.6379 17.7781C10.4358 17.8619 10.2191 17.905 10.0003 17.905C9.78155 17.905 9.5649 17.8619 9.36279 17.7781C9.16067 17.6944 8.97705 17.5716 8.82242 17.4168L5.28576 13.881C4.35344 12.9486 3.71853 11.7607 3.46133 10.4675C3.20412 9.17431 3.33616 7.83388 3.84076 6.61573C4.34535 5.39758 5.19984 4.35641 6.29616 3.62388C7.39248 2.89135 8.68139 2.50037 9.99992 2.50037C11.3184 2.50037 12.6074 2.89135 13.7037 3.62388C14.8 4.35641 15.6545 5.39758 16.1591 6.61573C16.6637 7.83388 16.7957 9.17431 16.5385 10.4675C16.2813 11.7607 15.6464 12.9486 14.7141 13.881V13.881Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12.5 9.16675C12.5 9.82979 12.2366 10.4657 11.7678 10.9345C11.2989 11.4034 10.663 11.6667 10 11.6667C9.33696 11.6667 8.70107 11.4034 8.23223 10.9345C7.76339 10.4657 7.5 9.82979 7.5 9.16675C7.5 8.50371 7.76339 7.86782 8.23223 7.39898C8.70107 6.93014 9.33696 6.66675 10 6.66675C10.663 6.66675 11.2989 6.93014 11.7678 7.39898C12.2366 7.86782 12.5 8.50371 12.5 9.16675V9.16675Z"
          stroke="currentColor"
          fill="transparent"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  mPriceTag: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.83419 2C9.46529 2 10.0705 2.25071 10.5168 2.69696L17.303 9.48319C18.2323 10.4125 18.2323 11.9191 17.303 12.8484L12.8484 17.303C11.9191 18.2323 10.4125 18.2323 9.48319 17.303L2.69696 10.5168C2.25071 10.0705 2 9.46529 2 8.83419V4.37959C2 3.06538 3.06538 2 4.37959 2H8.83419ZM8.83419 4H4.37959C4.16995 4 4 4.16995 4 4.37959V8.83419C4 8.93486 4.03999 9.03141 4.11118 9.10259L10.8974 15.8888C11.0456 16.0371 11.286 16.0371 11.4342 15.8888L15.8888 11.4342C16.0371 11.286 16.0371 11.0456 15.8888 10.8974L9.10259 4.11118C9.03141 4.03999 8.93486 4 8.83419 4ZM7.5 6C8.32843 6 9 6.67157 9 7.5C9 8.32843 8.32843 9 7.5 9C6.67157 9 6 8.32843 6 7.5C6 6.67157 6.67157 6 7.5 6Z"
        fill="#363940"
      />
    ),
  },
  mTrash: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M15 8C15.5523 8 16 8.44772 16 9V15C16 16.6569 14.6569 18 13 18H7C5.34315 18 4 16.6569 4 15V9C4 8.44772 4.44772 8 5 8H15ZM14 10H6V15C6 15.5523 6.44772 16 7 16H13C13.5523 16 14 15.5523 14 15V10ZM7 3C7 2.44772 7.44772 2 8 2H12C12.5523 2 13 2.44772 13 3V4H16C16.5523 4 17 4.44772 17 5C17 5.55228 16.5523 6 16 6H4C3.44772 6 3 5.55228 3 5C3 4.44772 3.44772 4 4 4H7V3Z"
      />
    ),
  },
  mComment: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M15 2C16.6569 2 18 3.34315 18 5V13C18 14.6569 16.6569 16 15 16H12.4142L10.7071 17.7071C10.3166 18.0976 9.68342 18.0976 9.29289 17.7071L7.58579 16H5C3.34315 16 2 14.6569 2 13V5C2 3.34315 3.34315 2 5 2H15ZM15 4H5C4.44772 4 4 4.44772 4 5V13C4 13.5523 4.44772 14 5 14H8C8.26522 14 8.51957 14.1054 8.70711 14.2929L10 15.5858L11.2929 14.2929C11.4804 14.1054 11.7348 14 12 14H15C15.5523 14 16 13.5523 16 13V5C16 4.44772 15.5523 4 15 4ZM13 10C13.5523 10 14 10.4477 14 11C14 11.5523 13.5523 12 13 12H7C6.44772 12 6 11.5523 6 11C6 10.4477 6.44772 10 7 10H13ZM13 6C13.5523 6 14 6.44772 14 7C14 7.55228 13.5523 8 13 8H7C6.44772 8 6 7.55228 6 7C6 6.44772 6.44772 6 7 6H13Z"
      />
    ),
  },
  mCheck: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.69207 12.995L5.04852 10.3515C4.5799 9.88286 3.8201 9.88286 3.35147 10.3515C2.88284 10.8201 2.88284 11.5799 3.35147 12.0485L6.95145 15.6485C7.46016 16.1572 8.29908 16.1071 8.74356 15.5414L17.1435 5.94143C17.553 5.42031 17.4625 4.66592 16.9413 4.25647C16.4202 3.84701 15.6658 3.93754 15.2564 4.45866L7.69207 12.995Z"
      />
    ),
  },
  mFile: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11 2C11.2652 2 11.5196 2.10536 11.7071 2.29289L15.7071 6.29289C15.8946 6.48043 16 6.73478 16 7V15C16 16.6569 14.6569 18 13 18H7C5.34315 18 4 16.6569 4 15V5C4 3.34315 5.34315 2 7 2H11ZM9.999 4H7C6.44772 4 6 4.44772 6 5V15C6 15.5523 6.44772 16 7 16H13C13.5523 16 14 15.5523 14 15V8H11C10.4872 8 10.0645 7.61396 10.0067 7.11662L10 7L9.999 4Z"
      />
    ),
  },
  mAddToCart: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.21875 15.1875C6.9954 15.1875 7.625 15.8171 7.625 16.5938C7.625 17.3704 6.9954 18 6.21875 18C5.4421 18 4.8125 17.3704 4.8125 16.5938C4.8125 15.8171 5.4421 15.1875 6.21875 15.1875ZM12.7812 15.1875C13.5579 15.1875 14.1875 15.8171 14.1875 16.5938C14.1875 17.3704 13.5579 18 12.7812 18C12.0046 18 11.375 17.3704 11.375 16.5938C11.375 15.8171 12.0046 15.1875 12.7812 15.1875ZM4.8125 3H2.9375L2.82817 3.00631C2.36191 3.06046 2 3.45672 2 3.9375C2 4.45527 2.41973 4.875 2.9375 4.875H3.875V10.5L3.87977 10.6653C3.96532 12.1416 5.18967 13.3125 6.6875 13.3125H12.7732L12.9434 13.3074C13.0631 13.3002 13.2014 13.2958 13.3489 13.2911C13.9572 13.2717 14.7234 13.2474 15 13C15.6226 12.4432 15.6118 11.5942 15.598 10.5119C15.5959 10.3466 15.5938 10.1759 15.5938 10H14.6562H13.7188H13.5V10.6677V11C13.3552 11.3441 13.1537 11.4375 12.7732 11.4375H6.6875L6.57817 11.4312C6.11191 11.377 5.75 10.9808 5.75 10.5V6.75H10V4.875H5.75V3.9375L5.74369 3.82817C5.68954 3.36191 5.29328 3 4.8125 3ZM15 2C15.5523 2 16 2.44772 16 3V4H17C17.5523 4 18 4.44772 18 5C18 5.55228 17.5523 6 17 6H16V7C16 7.55228 15.5523 8 15 8C14.4477 8 14 7.55228 14 7V6H13C12.4477 6 12 5.55228 12 5C12 4.44772 12.4477 4 13 4H14V3C14 2.44772 14.4477 2 15 2Z"
      />
    ),
  },
  mMinus: {
    viewBox: '0 0 20 20',
    svg: (
      <line
        x1="4.33337"
        y1="9.83331"
        x2="15.6667"
        y2="9.83331"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ),
  },
  mPencil: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2.5 16.75V13C2.5 12.6685 2.6317 12.3505 2.86612 12.1161L11.6161 3.36612C12.1043 2.87796 12.8957 2.87796 13.3839 3.36612L17.1339 7.11612C17.622 7.60427 17.622 8.39573 17.1339 8.88388L8.38388 17.6339C8.14946 17.8683 7.83152 18 7.5 18H3.75C3.05964 18 2.5 17.4404 2.5 16.75ZM12.5 6.01777L5 13.5178V15.5H6.98223L14.4822 8L12.5 6.01777Z"
      />
    ),
  },
  mDollar: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.2501 1.66679C14.8525 1.66679 18.5834 5.39775 18.5834 10.0001C18.5834 14.6025 14.8525 18.3335 10.2501 18.3335C5.64771 18.3335 1.91675 14.6025 1.91675 10.0001C1.91675 5.39775 5.64771 1.66679 10.2501 1.66679ZM10.2501 3.33345C6.56818 3.33345 3.58341 6.31822 3.58341 10.0001C3.58341 13.682 6.56818 16.6668 10.2501 16.6668C13.932 16.6668 16.9167 13.682 16.9167 10.0001C16.9167 6.31822 13.932 3.33345 10.2501 3.33345ZM10.5909 15.0001H9.85486C9.81421 15.001 9.77381 14.9922 9.736 14.9742C9.69819 14.9562 9.66371 14.9294 9.63454 14.8953C9.60538 14.8612 9.58212 14.8204 9.56609 14.7755C9.55006 14.7305 9.54159 14.6821 9.54116 14.6332V13.8531L9.35457 13.8297C8.79539 13.7588 8.25565 13.5697 7.9023 13.3215C7.76469 13.2269 7.71571 13.0257 7.7746 12.8486L8.05915 12.0921C8.07512 12.0455 8.09913 12.0035 8.12959 11.9689C8.16004 11.9343 8.19625 11.9079 8.23582 11.8914C8.26742 11.8779 8.30049 11.87 8.33398 11.868C8.38296 11.868 8.43213 11.8914 8.48111 11.9148C8.91279 12.1867 9.48207 12.3521 10.0218 12.3521C10.649 12.3521 11.1102 12.0446 11.1102 11.6192C11.1102 11.3474 10.9826 11.0043 10.0014 10.626C8.94233 10.2242 7.86343 9.63297 7.86343 8.3208C7.86343 7.32778 8.46148 6.54768 9.48207 6.24023L9.63892 6.19343V5.36537C9.63959 5.26858 9.67187 5.176 9.72879 5.10765C9.78572 5.0393 9.8627 5.00067 9.9431 5.00012H10.6692C10.7496 5.00073 10.8265 5.03939 10.8834 5.10773C10.9403 5.17608 10.9726 5.26862 10.9732 5.36537V6.12324L11.1501 6.14664C11.5549 6.19718 11.9499 6.32869 12.3188 6.53575C12.3902 6.58236 12.4458 6.65762 12.4758 6.74844C12.5048 6.8432 12.5048 6.94943 12.4659 7.03226L12.1715 7.77702C12.1552 7.82093 12.1317 7.86046 12.1025 7.89327C12.0733 7.92608 12.0389 7.95151 12.0014 7.96804C11.9639 7.98458 11.924 7.99189 11.8841 7.98954C11.8442 7.9872 11.8051 7.97524 11.7691 7.95438C11.5827 7.84792 11.1312 7.59967 10.4247 7.59967C9.77634 7.59967 9.49179 7.90712 9.49179 8.20264C9.49179 8.53326 9.78625 8.75811 10.7287 9.13716C11.7295 9.52721 12.7501 10.1183 12.7501 11.5486C12.7501 12.317 12.2988 13.3575 11.0525 13.7356L10.8955 13.7824V14.6336C10.8951 14.7307 10.8629 14.8237 10.8059 14.8923C10.7488 14.961 10.6716 14.9997 10.5909 15.0001Z"
      />
    ),
  },
  mAdd: {
    viewBox: '0 0 20 20',
    svg: (
      <>
        <line
          x1="10.1665"
          y1="4.33334"
          x2="10.1665"
          y2="15.6667"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <line
          x1="4.3335"
          y1="9.83334"
          x2="15.6668"
          y2="9.83334"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  mKebab: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 14C11.1046 14 12 14.8954 12 16C12 17.1046 11.1046 18 10 18C8.89543 18 8 17.1046 8 16C8 14.8954 8.89543 14 10 14ZM10 8C11.1046 8 12 8.89543 12 10C12 11.1046 11.1046 12 10 12C8.89543 12 8 11.1046 8 10C8 8.89543 8.89543 8 10 8ZM10 2C11.1046 2 12 2.89543 12 4C12 5.10457 11.1046 6 10 6C8.89543 6 8 5.10457 8 4C8 2.89543 8.89543 2 10 2Z"
      />
    ),
  },
  mCart: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.5 17C7.32843 17 8 17.6716 8 18.5C8 19.3284 7.32843 20 6.5 20C5.67157 20 5 19.3284 5 18.5C5 17.6716 5.67157 17 6.5 17ZM15.5 17C16.3284 17 17 17.6716 17 18.5C17 19.3284 16.3284 20 15.5 20C14.6716 20 14 19.3284 14 18.5C14 17.6716 14.6716 17 15.5 17ZM1 0H3C3.4556 0 3.84831 0.30684 3.96506 0.737739L3.98837 0.847943L4.4733 4H19C19.5998 4 20.0549 4.52068 19.9951 5.10035L19.9762 5.21693L18.3242 12.6508C18.0325 13.9637 16.9059 14.9143 15.5779 14.9945L15.3957 15H6.71584C5.29435 15 4.07697 14.0044 3.78265 12.6299L3.75072 12.4562L2.14208 2H1C0.447715 2 0 1.55228 0 1C0 0.487164 0.38604 0.0644928 0.883379 0.00672773L1 0H3H1ZM17.7534 6H4.781L5.72747 12.1521C5.79674 12.6024 6.15973 12.9438 6.60337 12.9937L6.71584 13H15.3957C15.8253 13 16.2021 12.7265 16.3402 12.3285L16.3718 12.2169L17.7534 6Z"
      />
    ),
  },
  mDelivery: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        d="M7.191 14.5551C7.191 14.8661 7.06744 15.1644 6.84751 15.3843C6.62758 15.6043 6.32929 15.7278 6.01825 15.7278C5.70722 15.7278 5.40893 15.6043 5.189 15.3843C4.96906 15.1644 4.84551 14.8661 4.84551 14.5551M7.191 14.5551C7.191 14.2441 7.06744 13.9458 6.84751 13.7258C6.62758 13.5059 6.32929 13.3823 6.01825 13.3823C5.70722 13.3823 5.40893 13.5059 5.189 13.7258C4.96906 13.9458 4.84551 14.2441 4.84551 14.5551M7.191 14.5551H11.882M4.84551 14.5551H3.37957C3.1463 14.5551 2.92258 14.4624 2.75763 14.2975C2.59268 14.1325 2.50001 13.9088 2.50001 13.6755V11.0369M15.9866 14.5551C15.9866 14.8661 15.863 15.1644 15.6431 15.3843C15.4232 15.6043 15.1249 15.7278 14.8139 15.7278C14.5028 15.7278 14.2045 15.6043 13.9846 15.3843C13.7647 15.1644 13.6411 14.8661 13.6411 14.5551M15.9866 14.5551C15.9866 14.2441 15.863 13.9458 15.6431 13.7258C15.4232 13.5059 15.1249 13.3823 14.8139 13.3823C14.5028 13.3823 14.2045 13.5059 13.9846 13.7258C13.7647 13.9458 13.6411 14.2441 13.6411 14.5551M15.9866 14.5551H16.8662C17.3517 14.5551 17.7488 14.161 17.7184 13.6763C17.5612 11.0944 16.6917 8.60658 15.2063 6.48894C15.0649 6.29058 14.8801 6.12697 14.6661 6.01049C14.4521 5.894 14.2144 5.82769 13.971 5.81657H11.882M13.6411 14.5551H11.882M11.882 5.81657V5.06757C11.882 4.62349 11.5521 4.24821 11.1103 4.20287C8.50444 3.93238 5.87756 3.93238 3.27168 4.20287C3.05883 4.2259 2.86209 4.32708 2.71954 4.48681C2.577 4.64655 2.49877 4.85348 2.50001 5.06757V11.0369M11.882 5.81657V11.0369M11.882 14.5551V11.0369M11.882 11.0369H2.50001"
        stroke="currentColor"
        fill="none"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ),
  },
  mChatDefault: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.26309 14.5001L3.71852 17.3358C3.22745 17.7286 2.5 17.379 2.5 16.7501V4.75012C2.5 3.50748 3.50736 2.50012 4.75 2.50012H15.25C16.4926 2.50012 17.5 3.50748 17.5 4.75012V12.2501C17.5 13.4928 16.4926 14.5001 15.25 14.5001H7.26309ZM3.99984 15.1896L6.53132 13.1645C6.6643 13.0581 6.82953 13.0001 6.99984 13.0001H15.2498C15.6641 13.0001 15.9998 12.6643 15.9998 12.2501V4.75011C15.9998 4.3359 15.6641 4.00011 15.2498 4.00011H4.74984C4.33562 4.00011 3.99984 4.3359 3.99984 4.75011V15.1896Z"
      />
    ),
  },
  mUser: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7 16H17C19.7614 16 22 18.2386 22 21C22 21.5523 21.5523 22 21 22C20.4872 22 20.0645 21.614 20.0067 21.1166L19.9949 20.8237C19.907 19.3072 18.6928 18.093 17.1763 18.0051L17 18H7C5.34315 18 4 19.3431 4 21C4 21.5523 3.55228 22 3 22C2.44772 22 2 21.5523 2 21C2 18.3112 4.12231 16.1182 6.78311 16.0046L7 16H17H7ZM12 2C15.3137 2 18 4.68629 18 8C18 11.3137 15.3137 14 12 14C8.68629 14 6 11.3137 6 8C6 4.68629 8.68629 2 12 2ZM12 4C9.79086 4 8 5.79086 8 8C8 10.2091 9.79086 12 12 12C14.2091 12 16 10.2091 16 8C16 5.79086 14.2091 4 12 4Z"
      />
    ),
  },
  mUsers: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.83329 11.6667H6.66663C8.02974 11.6667 9.23996 12.3213 10.0001 13.3332C10.72 12.3745 11.844 11.7366 13.1194 11.6721L13.3333 11.6667H14.1666C16.4678 11.6667 18.3333 13.5322 18.3333 15.8334C18.3333 16.2936 17.9602 16.6667 17.5 16.6667C17.0726 16.6667 16.7204 16.345 16.6722 15.9305L16.6624 15.6865C16.5892 14.4227 15.5773 13.4108 14.3135 13.3376L14.1666 13.3334H13.3333C11.9526 13.3334 10.8333 14.4527 10.8333 15.8334C10.8333 16.2607 10.5116 16.613 10.0971 16.6611L9.99996 16.6667C9.5726 16.6667 9.22037 16.345 9.17223 15.9305L9.16238 15.6865C9.08916 14.4227 8.07725 13.4108 6.81352 13.3376L6.66663 13.3334H5.83329C4.45258 13.3334 3.33329 14.4527 3.33329 15.8334C3.33329 16.2936 2.9602 16.6667 2.49996 16.6667C2.03972 16.6667 1.66663 16.2936 1.66663 15.8334C1.66663 13.5927 3.43521 11.7652 5.65255 11.6705L5.83329 11.6667ZM5.83329 3.33336C7.67424 3.33336 9.16663 4.82575 9.16663 6.6667C9.16663 8.50765 7.67424 10 5.83329 10C3.99234 10 2.49996 8.50765 2.49996 6.6667C2.49996 4.82575 3.99234 3.33336 5.83329 3.33336ZM14.1666 3.33336C16.0076 3.33336 17.5 4.82575 17.5 6.6667C17.5 8.50765 16.0076 10 14.1666 10C12.3257 10 10.8333 8.50765 10.8333 6.6667C10.8333 4.82575 12.3257 3.33336 14.1666 3.33336ZM5.83329 5.00003C4.91282 5.00003 4.16663 5.74622 4.16663 6.6667C4.16663 7.58717 4.91282 8.33336 5.83329 8.33336C6.75377 8.33336 7.49996 7.58717 7.49996 6.6667C7.49996 5.74622 6.75377 5.00003 5.83329 5.00003ZM14.1666 5.00003C13.2462 5.00003 12.5 5.74622 12.5 6.6667C12.5 7.58717 13.2462 8.33336 14.1666 8.33336C15.0871 8.33336 15.8333 7.58717 15.8333 6.6667C15.8333 5.74622 15.0871 5.00003 14.1666 5.00003Z"
      />
    ),
  },
  mCircle: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M18.3334 10C18.3334 14.6024 14.6025 18.3333 10.0001 18.3333C5.39771 18.3333 1.66675 14.6024 1.66675 10C1.66675 5.39763 5.39771 1.66667 10.0001 1.66667C14.6025 1.66667 18.3334 5.39763 18.3334 10ZM16.6667 10C16.6667 13.6819 13.682 16.6667 10.0001 16.6667C6.31818 16.6667 3.33341 13.6819 3.33341 10C3.33341 6.31811 6.31818 3.33334 10.0001 3.33334C13.682 3.33334 16.6667 6.31811 16.6667 10Z"
      />
    ),
  },
  mSettings: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.8976 2.00199L13.0201 2.00189C13.0843 2.00487 13.1406 2.01221 13.2197 2.02944C13.8002 2.15583 14.0209 2.36426 14.5611 3.64074L14.6491 3.85131L14.9886 4.69248L15.0165 4.70396L15.4539 4.50595C16.4066 4.07996 16.8976 3.90052 17.2278 3.85046L17.2962 3.84167L17.4209 3.83494C17.7182 3.83494 18.021 3.94025 18.2528 4.1236L18.3624 4.22165L19.7193 5.57296L19.8137 5.67265C20.2399 6.14218 20.2797 6.47505 19.8082 7.68823L19.6513 8.07961L19.2815 8.95458L19.2886 8.97166L20.1357 9.288L20.5441 9.4494C21.599 9.88072 21.8158 10.1106 21.9568 10.6386C21.9852 10.7451 21.9953 10.8176 21.9986 10.9077L22 11.0055V12.9132C22 13.0488 21.9954 13.1254 21.962 13.2594C21.8216 13.8231 21.6132 14.0378 20.3455 14.5733L20.1363 14.6606L19.301 14.9977L19.2952 15.0118L19.5972 15.6717C20.3484 17.3412 20.3746 17.7072 19.8809 18.257L19.8192 18.324L18.4263 19.7158C18.1769 19.9679 17.8217 20.1067 17.4779 20.1067C17.186 20.1067 16.7275 19.9679 15.9459 19.6545L15.5275 19.4831L15.0509 19.2815L15.0236 19.2928L14.7773 19.9533C14.1744 21.5419 13.9678 21.7998 13.3484 21.9596C13.2569 21.9832 13.1914 21.9933 13.1171 21.9974L12.9945 22H11.0856C10.9665 22 10.8993 21.9964 10.7809 21.9708C10.2007 21.845 9.97914 21.6361 9.43788 20.3589L9.34966 20.1482L9.0096 19.3066L8.98344 19.2957L8.34815 19.5813C7.332 20.0287 6.89179 20.1648 6.57806 20.1648C6.28426 20.1648 5.98508 20.0616 5.74708 19.8765L5.63349 19.7772L4.27843 18.4239L4.12369 18.2552C3.75208 17.8179 3.73381 17.4789 4.18994 16.3119L4.42762 15.7277L4.71777 15.0457L4.71172 15.031L4.05484 14.7862C2.73708 14.2862 2.33098 14.0559 2.138 13.6354L2.0858 13.503L2.0422 13.3566C2.01752 13.2629 2.00699 13.1958 2.00272 13.1195L2 12.9935V11.0847C2 10.948 2.00474 10.8707 2.03877 10.7355C2.16566 10.2316 2.34576 10.0077 3.29447 9.58261L3.68766 9.41282L4.69733 9.00451L4.70425 8.9877L4.32674 8.15478C3.70383 6.73991 3.65224 6.30911 4.04525 5.82796L4.09903 5.76483L4.22636 5.62987L5.57616 4.28313C5.82346 4.03446 6.1772 3.89427 6.5215 3.89427C6.76776 3.89427 7.12934 3.99171 7.70805 4.2109L8.29026 4.4417L8.95028 4.71832L8.97734 4.70707L9.22549 4.04225C9.83405 2.44226 10.0513 2.17469 10.6932 2.03099C10.7742 2.01286 10.8319 2.00513 10.8976 2.00199ZM12.523 4H11.4081L11.2509 4.36853C11.1912 4.51504 11.1238 4.68617 11.0478 4.88481L10.6993 5.81694C10.6157 6.03762 10.4574 6.2204 10.2538 6.33473L10.1483 6.38604L9.3292 6.7269C9.1224 6.81295 8.89398 6.82661 8.6804 6.768L8.55432 6.72416L7.58525 6.31569L7.06181 6.10695L6.70998 5.9771L5.91977 6.76552L6.01277 7.00042C6.04911 7.0889 6.09094 7.18782 6.13863 7.29803L6.2997 7.6633L6.70366 8.5464C6.80042 8.75669 6.82065 8.99264 6.76353 9.2141L6.71997 9.34493L6.38503 10.1588C6.29635 10.3743 6.13575 10.5509 5.93242 10.6598L5.82733 10.7085L4.73976 11.1457L4.19609 11.3787L4 11.4695V12.5997L4.23463 12.7014C4.32346 12.7386 4.42321 12.7791 4.53477 12.8233L4.90579 12.9672L5.80732 13.302C6.03461 13.384 6.22321 13.5449 6.34018 13.7532L6.39255 13.8612L6.72754 14.6728C6.81356 14.8812 6.82615 15.1112 6.76566 15.3257L6.7206 15.4523L6.33182 16.3646L6.088 16.9664L5.96921 17.286L6.75948 18.0752L6.99479 17.9822C7.08146 17.9469 7.17645 17.9073 7.28 17.8631L8.00585 17.543L8.5293 17.3021C8.74835 17.1974 8.99663 17.1766 9.22796 17.2408L9.342 17.28L10.1659 17.6202C10.3843 17.7104 10.5625 17.8744 10.6707 18.0816L10.7189 18.1887L10.9239 18.7061L11.2157 19.4157L11.3846 19.8034L11.4762 20H12.5944L12.7541 19.6235L12.9621 19.0924L13.3007 18.1844C13.3843 17.9617 13.5438 17.7774 13.7492 17.6626L13.8556 17.6112L14.676 17.2726C14.8804 17.1883 15.1058 17.1745 15.317 17.2313L15.4417 17.2738L16.2143 17.6015L16.7781 17.8311L17.0867 17.9501L17.2903 18.0238L18.0815 17.2341L17.929 16.8578L17.7004 16.3363L17.2956 15.4539C17.1985 15.2432 17.1782 15.0068 17.2356 14.7849L17.2793 14.6538L17.6137 13.8434C17.7019 13.6296 17.861 13.4541 18.0624 13.3452L18.1665 13.2964L19.0876 12.9262L19.5578 12.729L19.6871 12.6728L20 12.5297V11.4006L19.6863 11.2667L19.2615 11.0986L18.1891 10.6994C17.9623 10.6169 17.7744 10.4557 17.658 10.2473L17.6059 10.1393L17.2715 9.32452C17.1862 9.11664 17.1738 8.88739 17.2341 8.67355L17.279 8.54739L17.5895 7.8215C17.6703 7.62954 17.7406 7.45931 17.8014 7.30886L17.9103 7.03372L18.029 6.71221L17.2384 5.92482L17.0029 6.01798C16.8727 6.07108 16.7235 6.1341 16.5542 6.20769L16.3782 6.28483L15.4554 6.7034C15.2454 6.7996 15.0101 6.81961 14.7891 6.76264L14.6586 6.71921L13.8352 6.38074C13.6196 6.29214 13.443 6.13159 13.334 5.92829L13.2853 5.82321L12.9942 5.09482L12.7832 4.58452L12.6663 4.31313L12.523 4ZM12.0005 8C14.2066 8 16 9.79302 16 11.9991C16 14.2054 14.2065 16 12.0005 16C9.79382 16 8 14.2057 8 11.9991C8 9.7927 9.79369 8 12.0005 8ZM12.0005 10C10.898 10 10 10.8975 10 11.9991C10 13.1013 10.8985 14 12.0005 14C13.1016 14 14 13.1011 14 11.9991C14 10.8977 13.1021 10 12.0005 10Z"
      />
    ),
  },
  lSync: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.9811 10.8001C6.59643 10.7904 7.11086 11.2456 7.18957 11.8412L7.19984 11.981L7.20264 12.1684C7.28448 14.7642 9.24561 16.7999 11.5993 16.7999C11.9338 16.7999 12.2624 16.7594 12.5815 16.6799L12.3515 16.4484C11.8828 15.9798 11.8828 15.22 12.3515 14.7514C12.8201 14.2827 13.5799 14.2827 14.0485 14.7514L16.4485 17.1514C16.9171 17.62 16.9171 18.3798 16.4485 18.8484L14.0485 21.2484C13.5799 21.7171 12.8201 21.7171 12.3515 21.2484C11.8828 20.7798 11.8828 20.02 12.3515 19.5514L12.8187 19.0843C12.4192 19.1609 12.0115 19.1999 11.5993 19.1999C7.99942 19.1999 5.06801 16.246 4.81694 12.5005L4.80339 12.2251L4.80014 12.0188C4.78971 11.3561 5.31844 10.8105 5.9811 10.8001ZM11.6485 2.75137C12.0811 3.18395 12.1144 3.86463 11.7483 4.33538L11.6485 4.44843L11.1811 4.9154C11.5805 4.83883 11.988 4.7999 12.4 4.7999C16.1748 4.7999 19.2 8.04125 19.2 11.9999C19.2 12.6626 18.6627 13.1999 18 13.1999C17.3372 13.1999 16.8 12.6626 16.8 11.9999C16.8 9.33114 14.8108 7.1999 12.4 7.1999C12.0657 7.1999 11.7372 7.24039 11.4181 7.31976L11.6485 7.55137C12.1171 8.02 12.1171 8.7798 11.6485 9.24843C11.2159 9.68101 10.5353 9.71429 10.0645 9.34826L9.95146 9.24843L7.55146 6.84843C7.11888 6.41585 7.08561 5.73517 7.45164 5.26442L7.55146 5.15137L9.95146 2.75137C10.4201 2.28275 11.1799 2.28275 11.6485 2.75137Z"
      />
    ),
  },
  lAddToCart: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.5 19C9.32843 19 10 19.6716 10 20.5C10 21.3284 9.32843 22 8.5 22C7.67157 22 7 21.3284 7 20.5C7 19.6716 7.67157 19 8.5 19ZM17.5 19C18.3284 19 19 19.6716 19 20.5C19 21.3284 18.3284 22 17.5 22C16.6716 22 16 21.3284 16 20.5C16 19.6716 16.6716 19 17.5 19ZM3 2H5C5.4556 2 5.84831 2.30684 5.96506 2.73774L5.98837 2.84794L6.4733 6H14V8L19 10H21L20.3242 14.6508C20.0325 15.9637 18.9059 16.9143 17.5779 16.9945L17.3957 17H8.71584C7.29435 17 6.07697 16.0044 5.78265 14.6299L5.75072 14.4562L4.14208 4H3C2.44772 4 2 3.55228 2 3C2 2.48716 2.38604 2.06449 2.88338 2.00673L3 2ZM19 10L16.5 9L14 8H6.781L7.72747 14.1521C7.79674 14.6024 8.15973 14.9438 8.60337 14.9937L8.71584 15H17.3957C17.8253 15 18.2021 14.7265 18.3402 14.3285L18.3718 14.2169L19 10ZM19 2C19.5523 2 20 2.44772 20 3V4H21C21.5523 4 22 4.44772 22 5C22 5.55228 21.5523 6 21 6H20V7C20 7.55228 19.5523 8 19 8C18.4477 8 18 7.55228 18 7V6H17C16.4477 6 16 5.55228 16 5C16 4.44772 16.4477 4 17 4H18V3C18 2.44772 18.4477 2 19 2Z"
      />
    ),
  },
  lCart: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.5 19C9.32843 19 10 19.6716 10 20.5C10 21.3284 9.32843 22 8.5 22C7.67157 22 7 21.3284 7 20.5C7 19.6716 7.67157 19 8.5 19ZM17.5 19C18.3284 19 19 19.6716 19 20.5C19 21.3284 18.3284 22 17.5 22C16.6716 22 16 21.3284 16 20.5C16 19.6716 16.6716 19 17.5 19ZM3 2H5C5.4556 2 5.84831 2.30684 5.96506 2.73774L5.98837 2.84794L6.4733 6H21C21.5998 6 22.0549 6.52068 21.9951 7.10035L21.9762 7.21693L20.3242 14.6508C20.0325 15.9637 18.9059 16.9143 17.5779 16.9945L17.3957 17H8.71584C7.29435 17 6.07697 16.0044 5.78265 14.6299L5.75072 14.4562L4.14208 4H3C2.44772 4 2 3.55228 2 3C2 2.48716 2.38604 2.06449 2.88338 2.00673L3 2H5H3ZM19.7534 8H6.781L7.72747 14.1521C7.79674 14.6024 8.15973 14.9438 8.60337 14.9937L8.71584 15H17.3957C17.8253 15 18.2021 14.7265 18.3402 14.3285L18.3718 14.2169L19.7534 8Z"
      />
    ),
  },
  lSearch: {
    viewBox: '0 0 24 24',
    svg: (
      <>
        <circle
          cx="11"
          cy="11"
          r="7.5"
          strokeWidth="2"
          stroke="currentColor"
          style={{
            fill: 'transparent',
          }}
        />
        <path d="M20.331 21.7433C20.7415 22.1128 21.3738 22.0795 21.7433 21.669C22.1128 21.2585 22.0795 20.6262 21.669 20.2567L20.331 21.7433ZM15.331 17.2433L20.331 21.7433L21.669 20.2567L16.669 15.7567L15.331 17.2433Z" />
      </>
    ),
  },
  lMenu: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M21 17C21.5523 17 22 17.4477 22 18C22 18.5523 21.5523 19 21 19H3C2.44772 19 2 18.5523 2 18C2 17.4477 2.44772 17 3 17H21ZM21 11C21.5523 11 22 11.4477 22 12C22 12.5523 21.5523 13 21 13H3C2.44772 13 2 12.5523 2 12C2 11.4477 2.44772 11 3 11H21ZM21 5C21.5523 5 22 5.44772 22 6C22 6.55228 21.5523 7 21 7H3C2.44772 7 2 6.55228 2 6C2 5.44772 2.44772 5 3 5H21Z"
      />
    ),
  },
  lCalendarDays: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5 22C3.34315 22 2 20.6569 2 19V7C2 5.34315 3.34315 4 5 4H6V3C6 2.44772 6.44772 2 7 2C7.55228 2 8 2.44772 8 3V4H16V3C16 2.44772 16.4477 2 17 2C17.5523 2 18 2.44772 18 3V4H19C20.6569 4 22 5.34315 22 7V19C22 20.6569 20.6569 22 19 22H5ZM8 17H4V19C4 19.5523 4.44772 20 5 20H8V17ZM14 17H10V20H14V17ZM20 17H16V20H19C19.5523 20 20 19.5523 20 19V17ZM8 12H4V15H8V12ZM14 12H10V15H14V12ZM20 12H16V15H20V12ZM6 6H5C4.44772 6 4 6.44772 4 7V10H20V7C20 6.44772 19.5523 6 19 6H18V7C18 7.55228 17.5523 8 17 8C16.4477 8 16 7.55228 16 7V6H8V7C8 7.55228 7.55228 8 7 8C6.44772 8 6 7.55228 6 7V6Z"
      />
    ),
  },
  lHome: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.001 21.996C5.34448 21.996 4.0016 20.6531 4.0016 18.9966V12.9978H3.0018C2.11108 12.9978 1.665 11.9209 2.29484 11.291L11.293 2.29283C11.6835 1.90239 12.3165 1.90239 12.707 2.29283L21.7052 11.291C22.335 11.9209 21.8889 12.9978 20.9982 12.9978H19.9984V18.9966C19.9984 20.6531 18.6555 21.996 16.999 21.996H7.001ZM12 4.41374L5.35225 11.0615C5.73137 11.2036 6.00121 11.5693 6.00121 11.998V18.9966C6.00121 19.5488 6.44884 19.9964 7.00101 19.9964L9.00061 19.9954L9.00061 15.9972C9.00061 14.8929 9.89586 13.9976 11.0002 13.9976H12.9998C14.1042 13.9976 14.9994 14.8929 14.9994 15.9972L14.9994 19.9954L16.999 19.9964C17.5512 19.9964 17.9988 19.5488 17.9988 18.9966V11.998C17.9988 11.5693 18.2687 11.2036 18.6478 11.0615L12 4.41374ZM12.9998 15.9972H11.0002L11.0002 19.9954H12.9998L12.9998 15.9972Z"
      />
    ),
  },
  lDiscover: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M19 13C20.6569 13 22 14.3431 22 16V19C22 20.6569 20.6569 22 19 22H16C14.3431 22 13 20.6569 13 19V16C13 14.3431 14.3431 13 16 13H19ZM8 13C9.65685 13 11 14.3431 11 16V19C11 20.6569 9.65685 22 8 22H5C3.34315 22 2 20.6569 2 19V16C2 14.3431 3.34315 13 5 13H8ZM19 15H16C15.4477 15 15 15.4477 15 16V19C15 19.5523 15.4477 20 16 20H19C19.5523 20 20 19.5523 20 19V16C20 15.4477 19.5523 15 19 15ZM8 15H5C4.44772 15 4 15.4477 4 16V19C4 19.5523 4.44772 20 5 20H8C8.55228 20 9 19.5523 9 19V16C9 15.4477 8.55228 15 8 15ZM8 2C9.65685 2 11 3.34315 11 5V8C11 9.65685 9.65685 11 8 11H5C3.34315 11 2 9.65685 2 8V5C2 3.34315 3.34315 2 5 2H8ZM19 2C20.6569 2 22 3.34315 22 5V8C22 9.65685 20.6569 11 19 11H16C14.3431 11 13 9.65685 13 8V5C13 3.34315 14.3431 2 16 2H19ZM8 4H5C4.44772 4 4 4.44772 4 5V8C4 8.55228 4.44772 9 5 9H8C8.55228 9 9 8.55228 9 8V5C9 4.44772 8.55228 4 8 4ZM19 4H16C15.4477 4 15 4.44772 15 5V8C15 8.55228 15.4477 9 16 9H19C19.5523 9 20 8.55228 20 8V5C20 4.44772 19.5523 4 19 4Z"
      />
    ),
  },
  lIconWifi: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.75006 2.02829C8.66251 2.02837 10.5018 2.76321 11.8879 4.08094C11.9922 4.18267 12.1591 4.18139 12.2619 4.07806L13.2596 3.07116C13.3116 3.01875 13.3406 2.94776 13.3402 2.8739C13.3398 2.80004 13.3099 2.72939 13.2573 2.6776C9.61937 -0.808793 3.88016 -0.808793 0.242264 2.6776C0.189566 2.72935 0.159669 2.79998 0.159186 2.87384C0.158694 2.9477 0.187667 3.01871 0.239675 3.07116L1.23767 4.07806C1.34039 4.18155 1.50735 4.18283 1.61166 4.08094C2.99787 2.76313 4.83741 2.02828 6.75006 2.02829ZM6.75 5.3042C7.80077 5.30413 8.81404 5.6947 9.59293 6.4C9.69827 6.5001 9.86422 6.49793 9.96692 6.39511L10.9635 5.3882C11.0159 5.33539 11.0451 5.26374 11.0443 5.18929C11.0436 5.11483 11.013 5.04379 10.9594 4.99205C8.58757 2.78573 4.91442 2.78573 2.54256 4.99205C2.48898 5.04379 2.45841 5.11487 2.45771 5.18934C2.457 5.26382 2.48623 5.33546 2.53881 5.3882L3.53508 6.39511C3.63777 6.49793 3.80373 6.5001 3.90907 6.4C4.68745 5.69516 5.69992 5.30463 6.75 5.3042ZM8.66498 7.70726C8.71827 7.65495 8.74762 7.58297 8.7461 7.50831C8.74457 7.43365 8.71231 7.36292 8.65692 7.31284C7.55602 6.38169 5.94365 6.38169 4.84275 7.31284C4.78733 7.36288 4.755 7.43358 4.75342 7.50824C4.75184 7.58291 4.78115 7.65491 4.83441 7.70726L6.55852 9.4469C6.60905 9.49803 6.67794 9.5268 6.74984 9.5268C6.82172 9.5268 6.89061 9.49803 6.94114 9.4469L8.66498 7.70726Z"
      />
    ),
  },
  lIconCombinedShape: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12.3309 1.06067C12.3112 1.15988 12.3112 1.27919 12.3112 1.51781V8.10833C12.3112 8.34695 12.3112 8.46626 12.3309 8.56548C12.412 8.97292 12.7305 9.29142 13.1379 9.37246C13.2371 9.3922 13.3564 9.3922 13.595 9.3922C13.8337 9.3922 13.953 9.3922 14.0522 9.37246C14.4596 9.29142 14.7781 8.97292 14.8592 8.56548C14.8789 8.46626 14.8789 8.34695 14.8789 8.10833V1.51781C14.8789 1.27919 14.8789 1.15988 14.8592 1.06067C14.7781 0.653227 14.4596 0.334728 14.0522 0.253683C13.953 0.233948 13.8337 0.233948 13.595 0.233948C13.3564 0.233948 13.2371 0.233948 13.1379 0.253683C12.7305 0.334728 12.412 0.653227 12.3309 1.06067ZM8.28865 3.57202C8.28865 3.3334 8.28865 3.21409 8.30838 3.11487C8.38943 2.70743 8.70793 2.38894 9.11536 2.30789C9.21458 2.28816 9.33389 2.28816 9.57251 2.28816C9.81114 2.28816 9.93045 2.28816 10.0297 2.30789C10.4371 2.38894 10.7556 2.70743 10.8366 3.11487C10.8564 3.21409 10.8564 3.3334 10.8564 3.57202V8.10835C10.8564 8.34698 10.8564 8.46629 10.8366 8.5655C10.7556 8.97294 10.4371 9.29144 10.0297 9.37249C9.93045 9.39222 9.81114 9.39222 9.57251 9.39222C9.33389 9.39222 9.21458 9.39222 9.11536 9.37249C8.70793 9.29144 8.38943 8.97294 8.30838 8.5655C8.28865 8.46629 8.28865 8.34698 8.28865 8.10835V3.57202ZM4.37111 5.08346C4.35137 5.18267 4.35137 5.30199 4.35137 5.54061V8.10834C4.35137 8.34696 4.35137 8.46627 4.37111 8.56549C4.45215 8.97293 4.77065 9.29143 5.17809 9.37247C5.2773 9.39221 5.39662 9.39221 5.63524 9.39221C5.87386 9.39221 5.99317 9.39221 6.09239 9.37247C6.49983 9.29143 6.81833 8.97293 6.89937 8.56549C6.9191 8.46627 6.9191 8.34696 6.9191 8.10834V5.54061C6.9191 5.30199 6.9191 5.18267 6.89937 5.08346C6.81833 4.67602 6.49983 4.35752 6.09239 4.27648C5.99317 4.25674 5.87386 4.25674 5.63524 4.25674C5.39662 4.25674 5.2773 4.25674 5.17809 4.27648C4.77065 4.35752 4.45215 4.67602 4.37111 5.08346ZM0.262899 6.79528C0.243164 6.8945 0.243164 7.01381 0.243164 7.25243V8.10834C0.243164 8.34696 0.243164 8.46628 0.262899 8.56549C0.343944 8.97293 0.662443 9.29143 1.06988 9.37247C1.1691 9.39221 1.28841 9.39221 1.52703 9.39221C1.76565 9.39221 1.88496 9.39221 1.98418 9.37247C2.39162 9.29143 2.71012 8.97293 2.79116 8.56549C2.8109 8.46628 2.8109 8.34696 2.8109 8.10834V7.25243C2.8109 7.01381 2.8109 6.8945 2.79116 6.79528C2.71012 6.38784 2.39162 6.06934 1.98418 5.9883C1.88496 5.96856 1.76565 5.96856 1.52703 5.96856C1.28841 5.96856 1.1691 5.96856 1.06988 5.9883C0.662443 6.06934 0.343944 6.38784 0.262899 6.79528Z"
      />
    ),
  },
  lAdd: {
    viewBox: '0 0 24 24',
    svg: (
      <>
        <line
          x1="12"
          y1="5"
          x2="12"
          y2="19"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <line
          x1="5"
          y1="12"
          x2="19"
          y2="12"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  lIconBattery: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M16.3779 0.857422H3.69236C2.62407 0.857422 2.23669 0.968653 1.84614 1.17752C1.45559 1.38639 1.14908 1.69289 0.940216 2.08344C0.731348 2.47399 0.620117 2.86138 0.620117 3.92966V7.62816C0.620117 8.69644 0.731348 9.08383 0.940216 9.47438C1.14908 9.86493 1.45559 10.1714 1.84614 10.3803C2.23669 10.5892 2.62407 10.7004 3.69236 10.7004H16.3779C17.4462 10.7004 17.8336 10.5892 18.2241 10.3803C18.6147 10.1714 18.9212 9.86493 19.1301 9.47438C19.3389 9.08383 19.4502 8.69644 19.4502 7.62816V3.92966C19.4502 2.86138 19.3389 2.47399 19.1301 2.08344C18.9212 1.69289 18.6147 1.38639 18.2241 1.17752C17.8336 0.968653 17.4462 0.857422 16.3779 0.857422ZM2.24978 1.93227C2.55268 1.77028 2.84759 1.71333 3.69236 1.71333H16.3779C17.2227 1.71333 17.5176 1.77028 17.8205 1.93227C18.0619 2.06137 18.2462 2.2457 18.3753 2.48709C18.5373 2.78999 18.5943 3.0849 18.5943 3.92966V7.62816C18.5943 8.47293 18.5373 8.76784 18.3753 9.07073C18.2462 9.31212 18.0619 9.49645 17.8205 9.62555C17.5176 9.78754 17.2227 9.84449 16.3779 9.84449H3.69236C2.84759 9.84449 2.55268 9.78754 2.24978 9.62555C2.00839 9.49645 1.82407 9.31212 1.69497 9.07073C1.53298 8.76784 1.47603 8.47293 1.47603 7.62816V3.92966C1.47603 3.0849 1.53298 2.78999 1.69497 2.48709C1.82407 2.2457 2.00839 2.06137 2.24978 1.93227ZM20.3058 7.43935C20.3058 7.43935 21.5897 6.78593 21.5897 5.72753C21.5897 4.66914 20.3058 4.01571 20.3058 4.01571V7.43935ZM2.48129 3.2456C2.33203 3.53854 2.33203 3.92203 2.33203 4.689V6.86872C2.33203 7.63569 2.33203 8.01917 2.48129 8.31211C2.61259 8.56979 2.82209 8.77929 3.07977 8.91059C3.37271 9.05985 3.7562 9.05985 4.52316 9.05985H15.5473C16.3143 9.05985 16.6978 9.05985 16.9907 8.91059C17.2484 8.77929 17.4579 8.56979 17.5892 8.31211C17.7384 8.01917 17.7384 7.63568 17.7384 6.86872V4.689C17.7384 3.92203 17.7384 3.53854 17.5892 3.2456C17.4579 2.98792 17.2484 2.77842 16.9907 2.64713C16.6978 2.49786 16.3143 2.49786 15.5473 2.49786H4.52316C3.7562 2.49786 3.37271 2.49786 3.07977 2.64713C2.82209 2.77842 2.61259 2.98792 2.48129 3.2456Z"
      />
    ),
  },
  lShopFront: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.74998 4H15.25C15.25 4 16 3.84375 16 3C16 2.15625 15.25 2 15.25 2H4.74998C4.74998 2 3.99998 2.17188 3.99998 3C3.99998 3.82812 4.74998 4 4.74998 4ZM3.57089 18H16.4288C16.6688 18 16.857 17.7598 16.857 17.4533V13.2C16.857 13.2 16.6681 12.4 15.9062 12.4C15.1443 12.4 14.9999 13.2 14.9999 13.2V14.8V16H9.99991H4.99991V14.8V14V13.2C4.99991 13.2 4.83101 12.4 4.07803 12.4C3.31613 12.4 3.14273 13.2 3.14273 13.2V17.4533C3.14273 17.7515 3.33086 18 3.57089 18ZM2.00057 9.19282C2.00267 9.30012 2.01573 9.40589 2.03984 9.50592C2.31148 10.6126 3.41719 11.5 4.58922 11.5L4.66796 11.4995H4.73305C5.47937 11.4637 6.02623 11.0078 6.40792 10.6041C6.90698 11.0597 7.60155 11.3987 8.25583 11.3987C8.80078 11.3987 9.15847 11.1981 9.58136 10.8856C9.67901 10.8111 9.82228 10.6612 9.94038 10.5377C9.95261 10.5249 9.96457 10.5124 9.97619 10.5003H10.0068L10.0307 10.5094C10.0829 10.5635 10.1419 10.6414 10.2013 10.7197C10.2833 10.8277 10.3659 10.9366 10.432 10.9852C10.855 11.2976 11.3685 11.4426 11.8927 11.3987C12.5033 11.35 13.1099 11.0576 13.6099 10.5995C13.9876 10.9983 14.5338 11.4637 15.2863 11.4995C16.5193 11.558 17.6909 10.8615 17.9706 9.70807C18.0069 9.55749 18.0154 9.36436 18 9.1669C17.9999 9.09389 17.9912 9.02262 17.9741 8.95276C17.9366 8.72479 17.8685 8.50974 17.7761 8.36812C17.6429 8.11441 16.7981 6.39314 16.2369 5.23454C16.0922 4.93504 15.7849 4.8 15.5102 4.8H4.49073C4.21598 4.8 3.90874 4.93504 3.76397 5.23454C3.20662 6.38002 2.36719 8.09057 2.22527 8.36687C2.08262 8.57878 2.00724 8.8532 2.00073 9.12364C1.99981 9.14653 1.99976 9.16959 2.00057 9.19282ZM3.79819 5.25354L3.76366 5.23518C3.76376 5.23496 3.76387 5.23475 3.76397 5.23454C3.76407 5.23432 3.76418 5.23411 3.76428 5.23389L3.79874 5.25238C3.79856 5.25276 3.79837 5.25315 3.79819 5.25354ZM2.96721 8.85778C2.96728 8.85765 2.96736 8.85753 2.96744 8.85741C3.0232 8.86654 3.10724 8.8804 3.2339 8.90144L2.96721 8.85778ZM3.60383 9.07379C3.60383 9.07379 3.60333 9.06494 3.60369 9.04616C3.72721 8.85613 3.8778 8.57733 4.07948 8.18002C4.15916 8.02306 4.2153 7.88248 4.27153 7.74168C4.40604 7.40488 4.54106 7.06677 4.99998 6.49995H14.982C15.3035 6.95456 15.6318 7.60327 15.8572 8.04867C15.8797 8.09314 15.9012 8.13559 15.9215 8.17561C16.1231 8.57225 16.2737 8.85092 16.3972 9.04089C16.3975 9.05511 16.3973 9.06364 16.3971 9.06695C16.3971 9.068 16.3971 9.06853 16.3971 9.06853L16.3976 9.06695C16.3153 9.39659 15.9203 9.7131 15.3536 9.69168C15.2958 9.68902 15.186 9.64407 15.0196 9.50054C14.8756 9.37645 14.7354 9.22027 14.5976 9.06402C14.5859 9.05066 14.5741 9.03729 14.5624 9.02395L14.5623 9.02393L14.5585 9.01954L14.555 9.01566C14.4945 8.9487 14.404 8.84938 14.2664 8.75812L14.2649 8.75712C13.8294 8.47113 13.243 8.49275 12.8425 8.84075L12.8382 8.84445L12.8341 8.84829C12.7672 8.90998 12.7122 8.97305 12.6696 9.02197L12.6588 9.03435L12.6332 9.06083L12.6146 9.08011L12.6097 9.08532C12.306 9.41193 11.9749 9.58054 11.7441 9.59763C11.6269 9.60648 11.5242 9.57692 11.4235 9.50399C11.4235 9.50395 11.4234 9.50389 11.4233 9.50382C11.4231 9.50354 11.4225 9.50304 11.4215 9.50213C11.4155 9.49662 11.398 9.48076 11.3663 9.44954C11.328 9.41193 11.2804 9.36346 11.2286 9.30962C11.1494 9.22726 11.064 9.136 10.9929 9.05817C10.9714 9.03465 10.9512 9.01236 10.9329 8.99192C10.624 8.64157 10.2601 8.55409 10.0068 8.55409H9.98197L9.97619 8.55428C9.73959 8.56183 9.38567 8.64063 9.07038 8.98933L9.06913 8.99072C9.05136 9.01056 9.03181 9.03216 9.011 9.05494C8.93927 9.13348 8.85259 9.22612 8.7723 9.30962C8.72053 9.36346 8.67292 9.41193 8.63465 9.44954C8.60291 9.48073 8.58547 9.49659 8.57939 9.50212L8.57937 9.50213C8.57793 9.50345 8.57748 9.50391 8.57735 9.50406C8.46726 9.58459 8.37355 9.60912 8.2658 9.59829L8.25581 9.59756C8.01969 9.58038 7.69242 9.41315 7.39862 9.09341L7.36493 9.05349C7.36461 9.05308 7.3643 9.05267 7.36399 9.05227C7.31139 8.98452 7.24388 8.90684 7.16136 8.83782C6.75676 8.48914 6.17763 8.46442 5.73968 8.75887C5.60509 8.8484 5.5093 8.95028 5.44019 9.03256L5.4254 9.04912L5.40777 9.06885C5.26749 9.22596 5.12849 9.38164 4.98371 9.50648C4.81851 9.64892 4.70841 9.69441 4.64708 9.69695L4.64556 9.69702C4.07643 9.72301 3.68465 9.40341 3.60279 9.07009L3.60383 9.07379ZM8.58118 9.50125C8.5799 9.50219 8.57863 9.50313 8.57735 9.50406C8.57727 9.50417 8.57732 9.50414 8.57732 9.50414L8.80076 9.82812L8.58118 9.50125ZM11.4236 9.50414C11.4236 9.50414 11.4237 9.50418 11.4235 9.50399C11.422 9.50293 11.4205 9.50185 11.4191 9.50076L11.2002 9.82812L11.4236 9.50414Z"
      />
    ),
  },
  lPencil: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3 19.5V15C3 14.6022 3.15804 14.2206 3.43934 13.9393L13.9393 3.43934C14.5251 2.85355 15.4749 2.85355 16.0607 3.43934L20.5607 7.93934C21.1464 8.52513 21.1464 9.47487 20.5607 10.0607L10.0607 20.5607C9.77936 20.842 9.39782 21 9 21H4.5C3.67157 21 3 20.3284 3 19.5ZM15 6.62132L6 15.6213V18H8.37868L17.3787 9L15 6.62132Z"
      />
    ),
  },
  lPlus: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5V11H5C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13H11V19C11 19.5523 11.4477 20 12 20C12.5523 20 13 19.5523 13 19V13H19C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11H13V5Z"
      />
    ),
  },
  lSettings: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.8976 2.00199L13.0201 2.00189C13.0843 2.00487 13.1406 2.01221 13.2197 2.02944C13.8002 2.15583 14.0209 2.36426 14.5611 3.64074L14.6491 3.85131L14.9886 4.69248L15.0165 4.70396L15.4539 4.50595C16.4066 4.07996 16.8976 3.90052 17.2278 3.85046L17.2962 3.84167L17.4209 3.83494C17.7182 3.83494 18.021 3.94025 18.2528 4.1236L18.3624 4.22165L19.7193 5.57296L19.8137 5.67265C20.2399 6.14218 20.2797 6.47505 19.8082 7.68823L19.6513 8.07961L19.2815 8.95458L19.2886 8.97166L20.1357 9.288L20.5441 9.4494C21.599 9.88072 21.8158 10.1106 21.9568 10.6386C21.9852 10.7451 21.9953 10.8176 21.9986 10.9077L22 11.0055V12.9132C22 13.0488 21.9954 13.1254 21.962 13.2594C21.8216 13.8231 21.6132 14.0378 20.3455 14.5733L20.1363 14.6606L19.301 14.9977L19.2952 15.0118L19.5972 15.6717C20.3484 17.3412 20.3746 17.7072 19.8809 18.257L19.8192 18.324L18.4263 19.7158C18.1769 19.9679 17.8217 20.1067 17.4779 20.1067C17.186 20.1067 16.7275 19.9679 15.9459 19.6545L15.5275 19.4831L15.0509 19.2815L15.0236 19.2928L14.7773 19.9533C14.1744 21.5419 13.9678 21.7998 13.3484 21.9596C13.2569 21.9832 13.1914 21.9933 13.1171 21.9974L12.9945 22H11.0856C10.9665 22 10.8993 21.9964 10.7809 21.9708C10.2007 21.845 9.97914 21.6361 9.43788 20.3589L9.34966 20.1482L9.0096 19.3066L8.98344 19.2957L8.34815 19.5813C7.332 20.0287 6.89179 20.1648 6.57806 20.1648C6.28426 20.1648 5.98508 20.0616 5.74708 19.8765L5.63349 19.7772L4.27843 18.4239L4.12369 18.2552C3.75208 17.8179 3.73381 17.4789 4.18994 16.3119L4.42762 15.7277L4.71777 15.0457L4.71172 15.031L4.05484 14.7862C2.73708 14.2862 2.33098 14.0559 2.138 13.6354L2.0858 13.503L2.0422 13.3566C2.01752 13.2629 2.00699 13.1958 2.00272 13.1195L2 12.9935V11.0847C2 10.948 2.00474 10.8707 2.03877 10.7355C2.16566 10.2316 2.34576 10.0077 3.29447 9.58261L3.68766 9.41282L4.69733 9.00451L4.70425 8.9877L4.32674 8.15478C3.70383 6.73991 3.65224 6.30911 4.04525 5.82796L4.09903 5.76483L4.22636 5.62987L5.57616 4.28313C5.82346 4.03446 6.1772 3.89427 6.5215 3.89427C6.76776 3.89427 7.12934 3.99171 7.70805 4.2109L8.29026 4.4417L8.95028 4.71832L8.97734 4.70707L9.22549 4.04225C9.83405 2.44226 10.0513 2.17469 10.6932 2.03099C10.7742 2.01286 10.8319 2.00513 10.8976 2.00199ZM12.523 4H11.4081L11.2509 4.36853C11.1912 4.51504 11.1238 4.68617 11.0478 4.88481L10.6993 5.81694C10.6157 6.03762 10.4574 6.2204 10.2538 6.33473L10.1483 6.38604L9.3292 6.7269C9.1224 6.81295 8.89398 6.82661 8.6804 6.768L8.55432 6.72416L7.58525 6.31569L7.06181 6.10695L6.70998 5.9771L5.91977 6.76552L6.01277 7.00042C6.04911 7.0889 6.09094 7.18782 6.13863 7.29803L6.2997 7.6633L6.70366 8.5464C6.80042 8.75669 6.82065 8.99264 6.76353 9.2141L6.71997 9.34493L6.38503 10.1588C6.29635 10.3743 6.13575 10.5509 5.93242 10.6598L5.82733 10.7085L4.73976 11.1457L4.19609 11.3787L4 11.4695V12.5997L4.23463 12.7014C4.32346 12.7386 4.42321 12.7791 4.53477 12.8233L4.90579 12.9672L5.80732 13.302C6.03461 13.384 6.22321 13.5449 6.34018 13.7532L6.39255 13.8612L6.72754 14.6728C6.81356 14.8812 6.82615 15.1112 6.76566 15.3257L6.7206 15.4523L6.33182 16.3646L6.088 16.9664L5.96921 17.286L6.75948 18.0752L6.99479 17.9822C7.08146 17.9469 7.17645 17.9073 7.28 17.8631L8.00585 17.543L8.5293 17.3021C8.74835 17.1974 8.99663 17.1766 9.22796 17.2408L9.342 17.28L10.1659 17.6202C10.3843 17.7104 10.5625 17.8744 10.6707 18.0816L10.7189 18.1887L10.9239 18.7061L11.2157 19.4157L11.3846 19.8034L11.4762 20H12.5944L12.7541 19.6235L12.9621 19.0924L13.3007 18.1844C13.3843 17.9617 13.5438 17.7774 13.7492 17.6626L13.8556 17.6112L14.676 17.2726C14.8804 17.1883 15.1058 17.1745 15.317 17.2313L15.4417 17.2738L16.2143 17.6015L16.7781 17.8311L17.0867 17.9501L17.2903 18.0238L18.0815 17.2341L17.929 16.8578L17.7004 16.3363L17.2956 15.4539C17.1985 15.2432 17.1782 15.0068 17.2356 14.7849L17.2793 14.6538L17.6137 13.8434C17.7019 13.6296 17.861 13.4541 18.0624 13.3452L18.1665 13.2964L19.0876 12.9262L19.5578 12.729L19.6871 12.6728L20 12.5297V11.4006L19.6863 11.2667L19.2615 11.0986L18.1891 10.6994C17.9623 10.6169 17.7744 10.4557 17.658 10.2473L17.6059 10.1393L17.2715 9.32452C17.1862 9.11664 17.1738 8.88739 17.2341 8.67355L17.279 8.54739L17.5895 7.8215C17.6703 7.62954 17.7406 7.45931 17.8014 7.30886L17.9103 7.03372L18.029 6.71221L17.2384 5.92482L17.0029 6.01798C16.8727 6.07108 16.7235 6.1341 16.5542 6.20769L16.3782 6.28483L15.4554 6.7034C15.2454 6.7996 15.0101 6.81961 14.7891 6.76264L14.6586 6.71921L13.8352 6.38074C13.6196 6.29214 13.443 6.13159 13.334 5.92829L13.2853 5.82321L12.9942 5.09482L12.7832 4.58452L12.6663 4.31313L12.523 4ZM12.0005 8C14.2066 8 16 9.79302 16 11.9991C16 14.2054 14.2065 16 12.0005 16C9.79382 16 8 14.2057 8 11.9991C8 9.7927 9.79369 8 12.0005 8ZM12.0005 10C10.898 10 10 10.8975 10 11.9991C10 13.1013 10.8985 14 12.0005 14C13.1016 14 14 13.1011 14 11.9991C14 10.8977 13.1021 10 12.0005 10Z"
      />
    ),
  },
  lChatDefault: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.7157 17.4L4.46223 20.8028C3.87294 21.2742 3 20.8547 3 20.1V5.7C3 4.20883 4.20883 3 5.7 3H18.3C19.7912 3 21 4.20883 21 5.7V14.7C21 16.1912 19.7912 17.4 18.3 17.4H8.7157ZM4.7998 18.2274L7.83758 15.7972C7.99716 15.6695 8.19544 15.6 8.3998 15.6H18.2998C18.7969 15.6 19.1998 15.197 19.1998 14.7V5.69999C19.1998 5.20293 18.7969 4.79999 18.2998 4.79999H5.6998C5.20275 4.79999 4.7998 5.20293 4.7998 5.69999V18.2274Z"
      />
    ),
  },
  lCartDefault: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.5 19C9.32843 19 10 19.6716 10 20.5C10 21.3284 9.32843 22 8.5 22C7.67157 22 7 21.3284 7 20.5C7 19.6716 7.67157 19 8.5 19ZM17.5 19C18.3284 19 19 19.6716 19 20.5C19 21.3284 18.3284 22 17.5 22C16.6716 22 16 21.3284 16 20.5C16 19.6716 16.6716 19 17.5 19ZM3 2H5C5.4556 2 5.84831 2.30684 5.96506 2.73774L5.98837 2.84794L6.4733 6H21C21.5998 6 22.0549 6.52068 21.9951 7.10035L21.9762 7.21693L20.3242 14.6508C20.0325 15.9637 18.9059 16.9143 17.5779 16.9945L17.3957 17H8.71584C7.29435 17 6.07697 16.0044 5.78265 14.6299L5.75072 14.4562L4.14208 4H3C2.44772 4 2 3.55228 2 3C2 2.48716 2.38604 2.06449 2.88338 2.00673L3 2H5H3ZM19.7534 8H6.781L7.72747 14.1521C7.79674 14.6024 8.15973 14.9438 8.60337 14.9937L8.71584 15H17.3957C17.8253 15 18.2021 14.7265 18.3402 14.3285L18.3718 14.2169L19.7534 8Z"
      />
    ),
  },
  sMenu: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M21 17C21.5523 17 22 17.4477 22 18C22 18.5523 21.5523 19 21 19H3C2.44772 19 2 18.5523 2 18C2 17.4477 2.44772 17 3 17H21ZM21 11C21.5523 11 22 11.4477 22 12C22 12.5523 21.5523 13 21 13H3C2.44772 13 2 12.5523 2 12C2 11.4477 2.44772 11 3 11H21ZM21 5C21.5523 5 22 5.44772 22 6C22 6.55228 21.5523 7 21 7H3C2.44772 7 2 6.55228 2 6C2 5.44772 2.44772 5 3 5H21Z"
      />
    ),
  },
  ArrowLeft: {
    viewBox: '0 0 24 24',
    svg: (
      <>
        <path
          d="M9 18L4 12L9 6"
          stroke="currentColor"
          strokeWidth="2"
          style={{
            fill: 'transparent',
          }}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M5 12H19"
          stroke="currentColor"
          strokeWidth="2"
          style={{
            fill: 'transparent',
          }}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  lUser: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7 16H17C19.7614 16 22 18.2386 22 21C22 21.5523 21.5523 22 21 22C20.4872 22 20.0645 21.614 20.0067 21.1166L19.9949 20.8237C19.907 19.3072 18.6928 18.093 17.1763 18.0051L17 18H7C5.34315 18 4 19.3431 4 21C4 21.5523 3.55228 22 3 22C2.44772 22 2 21.5523 2 21C2 18.3112 4.12231 16.1182 6.78311 16.0046L7 16H17H7ZM12 2C15.3137 2 18 4.68629 18 8C18 11.3137 15.3137 14 12 14C8.68629 14 6 11.3137 6 8C6 4.68629 8.68629 2 12 2ZM12 4C9.79086 4 8 5.79086 8 8C8 10.2091 9.79086 12 12 12C14.2091 12 16 10.2091 16 8C16 5.79086 14.2091 4 12 4Z"
      />
    ),
  },
  lLock: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        style={{
          fill: 'transparent',
        }}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M7.5 11H5.25C4.65326 11 4.08097 11.2107 3.65901 11.5858C3.23705 11.9609 3 12.4696 3 13V19C3 19.5304 3.23705 20.0391 3.65901 20.4142C4.08097 20.7893 4.65326 21 5.25 21H18.75C19.3467 21 19.919 20.7893 20.341 20.4142C20.7629 20.0391 21 19.5304 21 19V13C21 12.4696 20.7629 11.9609 20.341 11.5858C19.919 11.2107 19.3467 11 18.75 11H16.5M7.5 11V7C7.5 5.93913 7.97411 4.92172 8.81802 4.17157C9.66193 3.42143 10.8065 3 12 3V3C13.1935 3 14.3381 3.42143 15.182 4.17157C16.0259 4.92172 16.5 5.93913 16.5 7V11M7.5 11H16.5M12 15V17"
      />
    ),
  },
  lHelp: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2ZM12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4ZM12 16C12.5523 16 13 16.4477 13 17C13 17.5523 12.5523 18 12 18C11.4477 18 11 17.5523 11 17C11 16.4477 11.4477 16 12 16ZM12 6C14.2091 6 16 7.79086 16 10C16 11.7948 14.8179 13.3135 13.1898 13.8201L13 13.874V14C13 14.5523 12.5523 15 12 15C11.4872 15 11.0645 14.614 11.0067 14.1166L11 14V13C11 12.4872 11.386 12.0645 11.8834 12.0067L12.1493 11.9945C13.1841 11.9182 14 11.0544 14 10C14 8.89543 13.1046 8 12 8C10.8954 8 10 8.89543 10 10C10 10.5523 9.55228 11 9 11C8.44772 11 8 10.5523 8 10C8 7.79086 9.79086 6 12 6Z"
      />
    ),
  },
  lUsers: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7 14H8C9.63573 14 11.088 14.7855 12.0002 15.9998C12.864 14.8494 14.2129 14.0839 15.7433 14.0065L16 14H17C19.7614 14 22 16.2386 22 19C22 19.5523 21.5523 20 21 20C20.4872 20 20.0645 19.614 20.0067 19.1166L19.9949 18.8237C19.907 17.3072 18.6928 16.093 17.1763 16.0051L17 16H16C14.3431 16 13 17.3431 13 19C13 19.5128 12.614 19.9355 12.1166 19.9933L12 20C11.4872 20 11.0645 19.614 11.0067 19.1166L10.9949 18.8237C10.907 17.3072 9.69275 16.093 8.17627 16.0051L8 16H7C5.34315 16 4 17.3431 4 19C4 19.5523 3.55228 20 3 20C2.44772 20 2 19.5523 2 19C2 16.3112 4.12231 14.1182 6.78311 14.0046L7 14ZM7 4C9.20914 4 11 5.79086 11 8C11 10.2091 9.20914 12 7 12C4.79086 12 3 10.2091 3 8C3 5.79086 4.79086 4 7 4ZM17 4C19.2091 4 21 5.79086 21 8C21 10.2091 19.2091 12 17 12C14.7909 12 13 10.2091 13 8C13 5.79086 14.7909 4 17 4ZM7 6C5.89543 6 5 6.89543 5 8C5 9.10457 5.89543 10 7 10C8.10457 10 9 9.10457 9 8C9 6.89543 8.10457 6 7 6ZM17 6C15.8954 6 15 6.89543 15 8C15 9.10457 15.8954 10 17 10C18.1046 10 19 9.10457 19 8C19 6.89543 18.1046 6 17 6Z"
      />
    ),
  },
  lGraph: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M19 2C20.6569 2 22 3.34315 22 5V19C22 20.6569 20.6569 22 19 22H5C3.34315 22 2 20.6569 2 19V5C2 3.34315 3.34315 2 5 2H19ZM19 4H5C4.44772 4 4 4.44772 4 5V19C4 19.5523 4.44772 20 5 20H19C19.5523 20 20 19.5523 20 19V5C20 4.44772 19.5523 4 19 4ZM8 14C8.55228 14 9 14.4477 9 15V17C9 17.5523 8.55228 18 8 18C7.44772 18 7 17.5523 7 17V15C7 14.4477 7.44772 14 8 14ZM12 6C12.5523 6 13 6.44772 13 7V17C13 17.5523 12.5523 18 12 18C11.4477 18 11 17.5523 11 17V7C11 6.44772 11.4477 6 12 6ZM16 10C16.5523 10 17 10.4477 17 11V17C17 17.5523 16.5523 18 16 18C15.4477 18 15 17.5523 15 17V11C15 10.4477 15.4477 10 16 10Z"
      />
    ),
  },
  lDollar: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2ZM12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4ZM12.409 17.9999H11.5257C11.477 18.001 11.4285 17.9904 11.3831 17.9689C11.3377 17.9473 11.2963 17.9151 11.2614 17.8742C11.2264 17.8332 11.1984 17.7844 11.1792 17.7304C11.16 17.6764 11.1498 17.6184 11.1493 17.5597V16.6235L10.9254 16.5955C10.2544 16.5104 9.60668 16.2835 9.18266 15.9856C9.01753 15.8722 8.95875 15.6307 9.02942 15.4182L9.37088 14.5104C9.39005 14.4545 9.41886 14.4041 9.45541 14.3626C9.49196 14.321 9.53541 14.2893 9.58289 14.2695C9.62081 14.2533 9.66049 14.2438 9.70067 14.2414C9.75945 14.2414 9.81846 14.2695 9.87723 14.2976C10.3952 14.6238 11.0784 14.8224 11.7261 14.8224C12.4787 14.8224 13.0322 14.4534 13.0322 13.943C13.0322 13.6167 12.879 13.2051 11.7016 12.751C10.4307 12.2689 9.13601 11.5594 9.13601 9.98481C9.13601 8.79319 9.85368 7.85707 11.0784 7.48813L11.2666 7.43197V6.4383C11.2674 6.32215 11.3061 6.21106 11.3745 6.12904C11.4428 6.04701 11.5351 6.00066 11.6316 6H12.503C12.5994 6.00073 12.6917 6.04712 12.76 6.12913C12.8283 6.21115 12.867 6.3222 12.8678 6.4383V7.34774L13.08 7.37582C13.5658 7.43646 14.0398 7.59428 14.4825 7.84275C14.5681 7.89868 14.6348 7.98899 14.6709 8.09798C14.7057 8.21169 14.7057 8.33917 14.659 8.43856L14.3057 9.33228C14.2861 9.38497 14.258 9.43241 14.2229 9.47178C14.1879 9.51115 14.1466 9.54166 14.1016 9.56151C14.0566 9.58135 14.0087 9.59012 13.9609 9.58731C13.913 9.58449 13.8661 9.57014 13.8229 9.54511C13.5992 9.41736 13.0574 9.11945 12.2096 9.11945C11.4315 9.11945 11.0901 9.4884 11.0901 9.84302C11.0901 10.2398 11.4434 10.5096 12.5744 10.9644C13.7753 11.4325 15 12.1418 15 13.8582C15 14.7802 14.4584 16.0289 12.9629 16.4826L12.7745 16.5388V17.5602C12.774 17.6767 12.7354 17.7883 12.6669 17.8707C12.5985 17.953 12.5058 17.9995 12.409 17.9999Z"
      />
    ),
  },
  lFile: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14 2C14.2652 2 14.5196 2.10536 14.7071 2.29289L19.7071 7.29289C19.8946 7.48043 20 7.73478 20 8V19C20 20.6569 18.6569 22 17 22H7C5.34315 22 4 20.6569 4 19V5C4 3.34315 5.34315 2 7 2H14ZM11.999 4H7C6.44772 4 6 4.44772 6 5V19C6 19.5523 6.44772 20 7 20H17C17.5523 20 18 19.5523 18 19V10H13C12.4872 10 12.0645 9.61396 12.0067 9.11662L12 9L11.999 4ZM17.586 8L13.999 4.414L14 8H17.586Z"
      />
    ),
  },
  lUserAdd: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7 16H17C19.7614 16 22 18.2386 22 21C22 21.5523 21.5523 22 21 22C20.4872 22 20.0645 21.614 20.0067 21.1166L19.9949 20.8237C19.907 19.3072 18.6928 18.093 17.1763 18.0051L17 18H7C5.34315 18 4 19.3431 4 21C4 21.5523 3.55228 22 3 22C2.44772 22 2 21.5523 2 21C2 18.3112 4.12231 16.1182 6.78311 16.0046L7 16ZM12 2C12.4062 2 12.8078 2.04051 13.2004 2.12029C13.7416 2.23029 14.0912 2.75821 13.9812 3.29943C13.8712 3.84065 13.3432 4.19022 12.802 4.08022C12.5404 4.02705 12.2722 4 12 4C9.79086 4 8 5.79086 8 8C8 10.2091 9.79086 12 12 12C13.2748 12 14.4482 11.4008 15.2001 10.4004C15.5319 9.95889 16.1588 9.86999 16.6003 10.2018C17.0418 10.5336 17.1307 11.1605 16.7989 11.602C15.6731 13.0999 13.9104 14 12 14C8.68629 14 6 11.3137 6 8C6 4.68629 8.68629 2 12 2ZM19 2C19.5523 2 20 2.44772 20 3V4H21C21.5523 4 22 4.44772 22 5C22 5.55228 21.5523 6 21 6H20V7C20 7.55228 19.5523 8 19 8C18.4477 8 18 7.55228 18 7V6H17C16.4477 6 16 5.55228 16 5C16 4.44772 16.4477 4 17 4H18V3C18 2.44772 18.4477 2 19 2Z"
      />
    ),
  },
  lListBullet: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3 18C3.55228 18 4 18.4477 4 19C4 19.5523 3.55228 20 3 20C2.44772 20 2 19.5523 2 19C2 18.4477 2.44772 18 3 18ZM21 18C21.5523 18 22 18.4477 22 19C22 19.5523 21.5523 20 21 20H7C6.44772 20 6 19.5523 6 19C6 18.4477 6.44772 18 7 18H21ZM3 11C3.55228 11 4 11.4477 4 12C4 12.5523 3.55228 13 3 13C2.44772 13 2 12.5523 2 12C2 11.4477 2.44772 11 3 11ZM21 11C21.5523 11 22 11.4477 22 12C22 12.5523 21.5523 13 21 13H7C6.44772 13 6 12.5523 6 12C6 11.4477 6.44772 11 7 11H21ZM3 4C3.55228 4 4 4.44772 4 5C4 5.55228 3.55228 6 3 6C2.44772 6 2 5.55228 2 5C2 4.44772 2.44772 4 3 4ZM21 4C21.5523 4 22 4.44772 22 5C22 5.55228 21.5523 6 21 6H7C6.44772 6 6 5.55228 6 5C6 4.44772 6.44772 4 7 4H21Z"
      />
    ),
  },
  tag: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.5427 2C11.3316 2 12.0882 2.31338 12.646 2.87121L21.1288 11.354C22.2904 12.5156 22.2904 14.3989 21.1288 15.5605L15.5605 21.1288C14.3989 22.2904 12.5156 22.2904 11.354 21.1288L2.87121 12.646C2.31338 12.0882 2 11.3316 2 10.5427V4.97448C2 3.33172 3.33172 2 4.97448 2H10.5427ZM10.5427 4H4.97448C4.43629 4 4 4.43629 4 4.97448V10.5427C4 10.8012 4.10267 11.049 4.28542 11.2318L12.7682 19.7146C13.1488 20.0951 13.7658 20.0951 14.1463 19.7146L19.7146 14.1463C20.0951 13.7658 20.0951 13.1488 19.7146 12.7682L11.2318 4.28542C11.049 4.10267 10.8012 4 10.5427 4ZM8 6C9.10457 6 10 6.89543 10 8C10 9.10457 9.10457 10 8 10C6.89543 10 6 9.10457 6 8C6 6.89543 6.89543 6 8 6Z"
      />
    ),
  },
  lInsights: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M19 2C20.6569 2 22 3.34315 22 5V19C22 20.6569 20.6569 22 19 22H5C3.34315 22 2 20.6569 2 19V5C2 3.34315 3.34315 2 5 2H19ZM19 4H5C4.44772 4 4 4.44772 4 5V19C4 19.5523 4.44772 20 5 20H19C19.5523 20 20 19.5523 20 19V5C20 4.44772 19.5523 4 19 4ZM8 14C8.55228 14 9 14.4477 9 15V17C9 17.5523 8.55228 18 8 18C7.44772 18 7 17.5523 7 17V15C7 14.4477 7.44772 14 8 14ZM12 6C12.5523 6 13 6.44772 13 7V17C13 17.5523 12.5523 18 12 18C11.4477 18 11 17.5523 11 17V7C11 6.44772 11.4477 6 12 6ZM16 10C16.5523 10 17 10.4477 17 11V17C17 17.5523 16.5523 18 16 18C15.4477 18 15 17.5523 15 17V11C15 10.4477 15.4477 10 16 10Z"
      />
    ),
  },
  lClose: {
    viewBox: '0 0 24 24',
    svg: (
      <path d="M5.70711 4.29289C5.31658 3.90237 4.68342 3.90237 4.29289 4.29289C3.90237 4.68342 3.90237 5.31658 4.29289 5.70711L10.5858 12L4.29289 18.2929C3.90237 18.6834 3.90237 19.3166 4.29289 19.7071C4.68342 20.0976 5.31658 20.0976 5.70711 19.7071L12 13.4142L18.2929 19.7071C18.6834 20.0976 19.3166 20.0976 19.7071 19.7071C20.0976 19.3166 20.0976 18.6834 19.7071 18.2929L13.4142 12L19.7071 5.70711C20.0976 5.31658 20.0976 4.68342 19.7071 4.29289C19.3166 3.90237 18.6834 3.90237 18.2929 4.29289L12 10.5858L5.70711 4.29289Z" />
    ),
  },
  lNavigationBack: {
    viewBox: '0 0 24 24',
    svg: (
      <>
        <path d="M 8.9101563,5.0039062 A 1,1 0 0 0 8.2324219,5.359375 l -5,6 a 1.0001,1.0001 0 0 0 0,1.28125 l 5,6 A 1,1 0 0 0 9.640625,18.767578 1,1 0 0 0 9.7675781,17.359375 L 5.3007812,12 9.7675781,6.640625 A 1,1 0 0 0 9.640625,5.2324219 1,1 0 0 0 8.9101563,5.0039062 Z" />
        <path d="m 5,11 a 1,1 0 0 0 -1,1 1,1 0 0 0 1,1 h 14 a 1,1 0 0 0 1,-1 1,1 0 0 0 -1,-1 z" />
      </>
    ),
  },
  recurring: {
    viewBox: '0 0 16 16',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2.29289 3.29294L4.29289 1.29294C4.89547 0.69037 5.90718 1.07238 5.99402 1.88642L6 2.00005L6.00119 3.10031C8.28284 3.56404 10 5.58151 10 8.00005C10 10.7615 7.76142 13.0001 5 13.0001C2.23858 13.0001 0 10.7615 0 8.00005C0 7.44777 0.447715 7.00005 1 7.00005C1.55228 7.00005 2 7.44777 2 8.00005C2 9.65691 3.34315 11.0001 5 11.0001C6.65685 11.0001 8 9.65691 8 8.00005C8 6.69417 7.16562 5.58317 6.0009 5.17108L6 6.00005C6 6.85222 5.01449 7.29749 4.37747 6.78328L4.29289 6.70716L2.29289 4.70716C1.93241 4.34667 1.90468 3.77944 2.2097 3.38715L2.29289 3.29294L4.29289 1.29294L2.29289 3.29294Z"
      />
    ),
  },
  // banner icons
  lInputsInfo: {
    viewBox: '0 0 24 24',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2ZM12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4ZM12 10C12.5523 10 13 10.3918 13 10.875V16.125C13 16.6082 12.5523 17 12 17C11.4477 17 11 16.6082 11 16.125V10.875C11 10.3918 11.4477 10 12 10ZM12 7C12.5523 7 13 7.44772 13 8C13 8.55228 12.5523 9 12 9C11.4477 9 11 8.55228 11 8C11 7.44772 11.4477 7 12 7Z"
      />
    ),
  },
  lInputsSuccess: {
    viewBox: '0 0 24 24',
    svg: (
      <>
        <path
          d="M 12,2 C 6.4890026,2 2,6.4890026 2,12 2,17.510997 6.4890026,22 12,22 17.510997,22 22,17.510997 22,12 22,6.4890026 17.510997,2 12,2 Z m 0,2 c 4.430118,0 8,3.5698818 8,8 0,4.430118 -3.569882,8 -8,8 C 7.5698818,20 4,16.430118 4,12 4,7.5698818 7.5698818,4 12,4 Z"
          id="circle2"
        />
        <path
          d="M15.5309 8.1827C15.0504 7.84819 14.4029 7.98636 14.0826 8.48812L11.012 13.3312L9.68213 12.255C9.22955 11.8841 8.56808 11.9714 8.21994 12.4513C7.86484 12.9313 7.9484 13.6148 8.40794 13.9784L10.6291 15.7673C10.8031 15.92 11.0259 16 11.2627 16C11.3184 16 11.3741 15.9927 11.4298 15.9855C11.7153 15.9346 11.9729 15.7673 12.133 15.5128L15.8303 9.69526C16.1436 9.1935 16.0113 8.51721 15.5309 8.1827Z"
          id="path4"
        />
      </>
    ),
  },
  lInputsError: {
    viewBox: '0 0 24 24',
    svg: (
      <>
        <path d="m 12,2.1835938 c -1.029511,0 -2.0592092,0.5018614 -2.6191406,1.5039062 L 1.5039062,17.787109 C 0.40931182,19.74614 1.8789421,22.25 4.1230469,22.25 H 19.876953 c 2.2441,0 3.713652,-2.503852 2.619141,-4.462891 L 14.619141,3.6875 C 14.059209,2.6854552 13.029511,2.1835938 12,2.1835938 Z m 0,1.9335937 c 0.334837,0 0.669679,0.180978 0.873047,0.5449219 L 20.75,18.761719 C 21.145088,19.468877 20.687051,20.25 19.876953,20.25 H 4.1230469 C 3.3129347,20.25 2.8548759,19.468885 3.25,18.761719 L 11.126953,4.6621094 C 11.330321,4.2981655 11.665163,4.1171875 12,4.1171875 Z" />
        <path d="m 12,9 a 1,1 0 0 0 -1,1 v 4 a 1,1 0 0 0 1,1 1,1 0 0 0 1,-1 V 10 A 1,1 0 0 0 12,9 Z" />
        <path d="m 13,17 a 1,1 0 0 1 -1,1 1,1 0 0 1 -1,-1 1,1 0 0 1 1,-1 1,1 0 0 1 1,1 z" />
      </>
    ),
  },
  download: {
    viewBox: '0 0 20 20',
    svg: (
      <>
        <path d="M17 12C17.5128 12 17.9355 12.386 17.9933 12.8834L18 13V15C18 16.5977 16.7511 17.9037 15.1763 17.9949L15 18H5C3.40232 18 2.09634 16.7511 2.00509 15.1763L2 15V13C2 12.4477 2.44772 12 3 12C3.51284 12 3.93551 12.386 3.99327 12.8834L4 13V15C4 15.5128 4.38604 15.9355 4.88338 15.9933L5 16H15C15.5128 16 15.9355 15.614 15.9933 15.1166L16 15V13C16 12.4477 16.4477 12 17 12ZM10 2C10.5523 2 11 2.44772 11 3V10.585L12.2929 9.29289C12.6534 8.93241 13.2206 8.90468 13.6129 9.2097L13.7071 9.29289C14.0676 9.65338 14.0953 10.2206 13.7903 10.6129L13.7071 10.7071L10.7071 13.7071C10.3466 14.0676 9.77939 14.0953 9.3871 13.7903L9.29289 13.7071L6.29289 10.7071C5.90237 10.3166 5.90237 9.68342 6.29289 9.29289C6.65338 8.93241 7.22061 8.90468 7.6129 9.2097L7.70711 9.29289L9 10.585V3C9 2.44772 9.44772 2 10 2Z" />
      </>
    ),
  },
  mShare: {
    viewBox: '0 0 20 20',
    svg: (
      <>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M2.69995 10.6C2.69995 9.21926 3.81924 8.09998 5.19995 8.09998H5.99995C6.49701 8.09998 6.89995 8.50292 6.89995 8.99998C6.89995 9.49703 6.49701 9.89998 5.99995 9.89998H5.19995C4.81335 9.89998 4.49995 10.2134 4.49995 10.6V15.6C4.49995 15.9866 4.81335 16.3 5.19995 16.3H14.7999C15.1865 16.3 15.5 15.9866 15.5 15.6V10.6C15.5 10.2134 15.1866 9.89998 14.8 9.89998H14C13.5029 9.89998 13.1 9.49703 13.1 8.99998C13.1 8.50292 13.5029 8.09998 14 8.09998H14.8C16.1807 8.09998 17.2999 9.21926 17.2999 10.6V15.6C17.2999 16.9807 16.1807 18.1 14.7999 18.1H5.19995C3.81924 18.1 2.69995 16.9807 2.69995 15.6V10.6Z"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M6.34352 5.38445C6.00356 5.74707 6.02193 6.31662 6.38455 6.65658C6.74717 6.99654 7.31673 6.97816 7.65668 6.61554L9.1001 5.0759L9.1001 12.4C9.1001 12.8971 9.50304 13.3 10.0001 13.3C10.4972 13.3 10.9001 12.8971 10.9001 12.4L10.9001 5.07589L12.3435 6.61554C12.6835 6.97816 13.253 6.99654 13.6156 6.65658C13.9783 6.31662 13.9966 5.74707 13.6567 5.38445L10.6567 2.18445C10.4865 2.00296 10.2489 1.89999 10.0001 1.89999C9.75133 1.89999 9.51366 2.00296 9.34352 2.18445L6.34352 5.38445Z"
        />
      </>
    ),
  },
  directDebit: {
    viewBox: '0 0 20 20',
    svg: (
      <>
        <path d="M15 4C16.6569 4 18 5.34315 18 7V13C18 14.6569 16.6569 16 15 16H5C3.34315 16 2 14.6569 2 13V7C2 5.34315 3.34315 4 5 4H15ZM4 10V13C4 13.5523 4.44772 14 5 14H15C15.5523 14 16 13.5523 16 13V10H4ZM4 10H16V7C16 6.44772 15.5523 6 15 6H5C4.44772 6 4 6.44772 4 7V10Z" />
        <path d="M9.93632 12.096C9.58432 12.096 9.24432 12.056 8.91632 11.976C8.58832 11.896 8.31832 11.792 8.10632 11.664L8.56232 10.632C8.76232 10.748 8.98432 10.842 9.22832 10.914C9.47632 10.982 9.71632 11.016 9.94832 11.016C10.0843 11.016 10.1903 11.008 10.2663 10.992C10.3463 10.972 10.4043 10.946 10.4403 10.914C10.4763 10.878 10.4943 10.836 10.4943 10.788C10.4943 10.712 10.4523 10.652 10.3683 10.608C10.2843 10.564 10.1723 10.528 10.0323 10.5C9.89632 10.468 9.74632 10.436 9.58232 10.404C9.41832 10.368 9.25232 10.322 9.08432 10.266C8.92032 10.21 8.76832 10.136 8.62832 10.044C8.49232 9.952 8.38232 9.832 8.29832 9.684C8.21432 9.532 8.17232 9.344 8.17232 9.12C8.17232 8.86 8.24432 8.624 8.38832 8.412C8.53632 8.196 8.75432 8.024 9.04232 7.896C9.33432 7.768 9.69632 7.704 10.1283 7.704C10.4123 7.704 10.6923 7.734 10.9683 7.794C11.2443 7.854 11.4923 7.946 11.7123 8.07L11.2863 9.096C11.0783 8.992 10.8763 8.914 10.6803 8.862C10.4883 8.81 10.3003 8.784 10.1163 8.784C9.98032 8.784 9.87232 8.796 9.79232 8.82C9.71232 8.844 9.65432 8.876 9.61832 8.916C9.58632 8.956 9.57032 9 9.57032 9.048C9.57032 9.12 9.61232 9.178 9.69632 9.222C9.78032 9.262 9.89032 9.296 10.0263 9.324C10.1663 9.352 10.3183 9.382 10.4823 9.414C10.6503 9.446 10.8163 9.49 10.9803 9.546C11.1443 9.602 11.2943 9.676 11.4303 9.768C11.5703 9.86 11.6823 9.98 11.7663 10.128C11.8503 10.276 11.8923 10.46 11.8923 10.68C11.8923 10.936 11.8183 11.172 11.6703 11.388C11.5263 11.6 11.3103 11.772 11.0223 11.904C10.7343 12.032 10.3723 12.096 9.93632 12.096ZM9.64832 12.72V11.94H10.4943V12.72H9.64832ZM9.64832 7.86V7.08H10.4943V7.86H9.64832Z" />
      </>
    ),
  },
  tick: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.69207 8.99503L2.04852 6.35149C1.5799 5.88286 0.820099 5.88286 0.351471 6.35149C-0.117157 6.82012 -0.117157 7.57991 0.351471 8.04854L3.95145 11.6485C4.46016 12.1572 5.29908 12.1071 5.74356 11.5414L14.1435 1.94143C14.553 1.42031 14.4625 0.665923 13.9413 0.256468C13.4202 -0.152986 12.6658 -0.0624605 12.2564 0.458664L4.69207 8.99503Z"
      />
    ),
  },
  doubleTick: {
    viewBox: '0 0 20 20',
    svg: (
      <>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M5.47561 12.5964L3.51743 10.7166C3.17029 10.3833 2.60748 10.3833 2.26035 10.7166C1.91322 11.0498 1.91322 11.5901 2.26035 11.9234L4.927 14.4833C5.30383 14.8451 5.92525 14.8094 6.25449 14.4071L12.4767 7.58052C12.78 7.20995 12.7129 6.6735 12.3269 6.38233C11.9409 6.09116 11.3821 6.15553 11.0788 6.52611L5.47561 12.5964Z"
          fill="#737373"
        />
        <path
          d="M9.55227 11.4277L10.7754 12.5964L16.4046 6.52611C16.7094 6.15553 17.2708 6.09116 17.6586 6.38233C18.0464 6.6735 18.1138 7.20995 17.809 7.58052L11.5579 14.4071C11.2271 14.8094 10.6028 14.8451 10.2242 14.4833L8.3999 12.6L9.55227 11.4277Z"
          fill="#737373"
        />
      </>
    ),
  },
  delete: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M15 8C15.5523 8 16 8.44772 16 9V15C16 16.6569 14.6569 18 13 18H7C5.34315 18 4 16.6569 4 15V9C4 8.44772 4.44772 8 5 8H15ZM14 10H6V15C6 15.5523 6.44772 16 7 16H13C13.5523 16 14 15.5523 14 15V10ZM7 3C7 2.44772 7.44772 2 8 2H12C12.5523 2 13 2.44772 13 3V4H16C16.5523 4 17 4.44772 17 5C17 5.55228 16.5523 6 16 6H4C3.44772 6 3 5.55228 3 5C3 4.44772 3.44772 4 4 4H7V3Z"
        fill="#DD0000"
      />
    ),
  },
  drag: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7 14C8.10457 14 9 14.8954 9 16C9 17.1046 8.10457 18 7 18C5.89543 18 5 17.1046 5 16C5 14.8954 5.89543 14 7 14ZM13 14C14.1046 14 15 14.8954 15 16C15 17.1046 14.1046 18 13 18C11.8954 18 11 17.1046 11 16C11 14.8954 11.8954 14 13 14ZM7 8C8.10457 8 9 8.89543 9 10C9 11.1046 8.10457 12 7 12C5.89543 12 5 11.1046 5 10C5 8.89543 5.89543 8 7 8ZM13 8C14.1046 8 15 8.89543 15 10C15 11.1046 14.1046 12 13 12C11.8954 12 11 11.1046 11 10C11 8.89543 11.8954 8 13 8ZM7 2C8.10457 2 9 2.89543 9 4C9 5.10457 8.10457 6 7 6C5.89543 6 5 5.10457 5 4C5 2.89543 5.89543 2 7 2ZM13 2C14.1046 2 15 2.89543 15 4C15 5.10457 14.1046 6 13 6C11.8954 6 11 5.10457 11 4C11 2.89543 11.8954 2 13 2Z"
      />
    ),
  },
  sort: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9.70742 5.29271L9.79061 5.38691C10.0702 5.74651 10.0702 6.2531 9.79061 6.6127L9.70742 6.7069L9.61321 6.79009C9.25358 7.06969 8.74694 7.06969 8.38732 6.79009L8.2931 6.7069L6.99976 5.41468L7.00012 14.9997C7.00012 15.5125 6.61408 15.9352 6.11674 15.9929L6.00012 15.9997L5.88335 15.9929C5.42427 15.9396 5.06002 15.5754 5.0067 15.1163L4.99997 14.9997L4.99976 5.41468L3.70699 6.7069L3.61277 6.79009C3.22045 7.09511 2.65318 7.06738 2.29267 6.7069C1.93216 6.34642 1.90443 5.7792 2.20948 5.38691L2.29267 5.29271L5.29287 2.29275L5.41333 2.18992L5.49588 2.13595L5.61375 2.07717L5.71243 2.04189L5.85101 2.01088L5.94623 2.00104L6.05421 2.00132L6.14852 2.01088L6.28793 2.04198L6.41457 2.08952L6.51964 2.14523L6.60178 2.20098L6.66557 2.2534L6.70722 2.29275L9.70742 5.29271L9.79061 5.38691L9.70742 5.29271ZM17.7905 14.6131L17.7073 14.7073L14.7071 17.7072L14.6216 17.7835L14.5512 17.8346L14.4541 17.8913L14.3812 17.9249L14.3137 17.9499L14.2622 17.9653L14.1484 17.9891L14.0541 17.9987L13.9252 17.997L13.8509 17.9891L13.7123 17.9581L13.5868 17.9111L13.4958 17.864L13.4132 17.81L13.3702 17.776L13.2928 17.7072L10.2926 14.7073C9.90201 14.3167 9.90201 13.6836 10.2926 13.2931C10.6531 12.9326 11.2203 12.9049 11.6127 13.2099L11.7069 13.2931L12.9997 14.5853L12.9999 5.00029C12.9999 4.48746 13.3859 4.06478 13.8832 4.00702L14 4.00029C14.5523 4.00029 15 4.44801 15 5.00029L14.9997 14.5853L16.293 13.2931C16.6535 12.9326 17.2208 12.9049 17.6131 13.2099L17.7073 13.2931C18.0678 13.6535 18.0956 14.2208 17.7905 14.6131Z"
      />
    ),
  },
  filter: {
    viewBox: '0 0 20 20',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 11C11.3063 11 12.4175 11.8349 12.8293 13.0001L17 13C17.5523 13 18 13.4477 18 14C18 14.5523 17.5523 15 17 15L12.829 15.0009C12.4169 16.1656 11.3059 17 10 17C8.69412 17 7.58312 16.1656 7.17102 15.0009L3 15C2.44772 15 2 14.5523 2 14C2 13.4477 2.44772 13 3 13L7.17067 13.0001C7.58249 11.8349 8.69375 11 10 11ZM10 13C9.44772 13 9 13.4477 9 14C9 14.5523 9.44772 15 10 15C10.5523 15 11 14.5523 11 14C11 13.4477 10.5523 13 10 13ZM15 3C16.6569 3 18 4.34315 18 6C18 7.65685 16.6569 9 15 9C13.6941 9 12.5831 8.16562 12.171 7.0009L3 7C2.44772 7 2 6.55228 2 6C2 5.44772 2.44772 5 3 5L12.1707 5.00009C12.5825 3.83485 13.6937 3 15 3ZM15 5C14.4477 5 14 5.44772 14 6C14 6.55228 14.4477 7 15 7C15.5523 7 16 6.55228 16 6C16 5.44772 15.5523 5 15 5Z"
      />
    ),
  },
  trophy: {
    viewBox: '0 0 24 24',
    fill: 'transparent',
    svg: (
      <path
        d="M16.1899 18.2308H7.81006M16.1899 18.2308C16.9308 18.2308 17.6413 18.5225 18.1651 19.0419C18.6889 19.5612 18.9832 20.2656 18.9832 21H5.01676C5.01676 20.2656 5.31105 19.5612 5.8349 19.0419C6.35874 18.5225 7.06923 18.2308 7.81006 18.2308M16.1899 18.2308V15.1154C16.1899 14.5422 15.7216 14.0769 15.1425 14.0769H14.3315M7.81006 18.2308V15.1154C7.81006 14.5422 8.27933 14.0769 8.85754 14.0769H9.66946M14.3315 14.0769H9.66946M14.3315 14.0769C13.8085 13.1815 13.496 12.1807 13.4171 11.1489M9.66946 14.0769C10.1921 13.1814 10.5043 12.1806 10.5829 11.1489M13.4171 11.1489C14.3556 10.9342 15.2306 10.5078 15.9758 9.90277M13.4171 11.1489C12.4846 11.3622 11.5154 11.3622 10.5829 11.1489M10.5829 11.1489C9.64469 10.9341 8.76912 10.5076 8.02421 9.90277M5.71508 4.83323C4.80074 4.96523 3.89572 5.12585 3 5.31323C3.20763 6.52134 3.81339 7.62713 4.72248 8.45758C5.63157 9.28802 6.79271 9.79626 8.02421 9.90277M5.71508 4.83323V5.07692C5.71508 7.02277 6.61452 8.76 8.02421 9.90277M5.71508 4.83323V3.43477C7.76909 3.14769 9.86778 3 12 3C14.1331 3 16.2318 3.14769 18.2849 3.43385V4.83323M18.2849 4.83323V5.07692C18.2849 7.02277 17.3855 8.76 15.9758 9.90277M18.2849 4.83323C19.1949 4.9645 20.1004 5.12458 21 5.31323C20.7924 6.5212 20.1868 7.62689 19.2779 8.45731C18.369 9.28774 17.2071 9.79607 15.9758 9.90277"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ),
  },
  twitterIcon: {
    viewBox: '0 0 543.684 543.684',
    svg: (
      <path
        d="M527.657,106.697c-2.681,0.783-5.361,1.512-8.041,2.191c-16.384,4.137-17.89-1.322-6.028-13.366
c5.312-5.397,10.006-11.267,14.082-17.607c9.137-14.217,1.212-20.417-14.333-13.776c-5.545,2.369-11.182,4.517-16.897,6.432
c-16.017,5.379-38.746-2.735-53.018-11.787c-18.018-11.426-38.495-17.136-61.438-17.136c-32.137,0-59.529,11.334-82.192,33.984
c-22.656,22.662-33.99,50.062-33.99,82.191c0,4.394,0.251,8.855,0.747,13.378c0.814,7.362-11.585,12.699-28.317,10.336
c-36.194-5.11-70.582-16.077-103.171-32.889c-32.32-16.671-60.845-37.65-85.57-62.938C37.672,73.624,21.687,74.665,17.274,90.98
c-2.644,9.78-3.959,19.951-3.959,30.515c0,19.908,4.675,38.372,14.027,55.392c4.651,8.47,10.098,16.138,16.353,22.999
c10.521,11.549,8.911,18.25-5.734,14.144c-14.639-4.106-25.367-10.202-25.367-9.804s0,0.722,0,0.722
c0,28.048,8.807,52.693,26.432,73.911c10.857,13.072,23.47,23.17,37.834,30.282c15.147,7.503,22.203,11.688,13.733,12.784
c-5.11,0.661-10.251,0.991-15.422,0.991c-3.5,0-7.172-0.159-11.003-0.483c-6.059-0.514-7.148,12.111,2.038,26.298
c7.301,11.273,16.646,21.193,28.03,29.762c11.579,8.721,24.058,14.981,37.417,18.794c16.255,4.633,19.517,13.073,5.024,21.763
c-35.863,21.519-75.551,32.277-119.058,32.277c-4.902,0-9.578-0.11-14.045-0.324c-7.754-0.373-2.552,6.456,12.417,14.296
c46.775,24.499,97.43,36.738,151.972,36.738c41.237,0,79.964-6.529,116.176-19.596c36.199-13.066,67.136-30.576,92.791-52.516
c25.655-21.94,47.779-47.173,66.365-75.711c18.581-28.537,32.424-58.33,41.543-89.376c9.106-31.053,13.666-62.167,13.666-93.342
c0-2.809-0.024-5.331-0.067-7.552c-0.086-4.174,10.955-15.472,23.28-27.032c5.563-5.22,10.869-10.698,15.937-16.444
C548.833,107.78,543.882,101.966,527.657,106.697z"
      />
    ),
  },
  facebookIcon: {
    viewBox: '-5.5 0 32 32',
    svg: (
      <path d="M1.188 5.594h18.438c0.625 0 1.188 0.563 1.188 1.188v18.438c0 0.625-0.563 1.188-1.188 1.188h-18.438c-0.625 0-1.188-0.563-1.188-1.188v-18.438c0-0.625 0.563-1.188 1.188-1.188zM14.781 17.281h2.875l0.125-2.75h-3v-2.031c0-0.781 0.156-1.219 1.156-1.219h1.75l0.063-2.563s-0.781-0.125-1.906-0.125c-2.75 0-3.969 1.719-3.969 3.563v2.375h-2.031v2.75h2.031v7.625h2.906v-7.625z" />
    ),
  },
  instagramIcon: {
    svg: (
      <path
        d="M-64.5,273h-157c-27.3,0-49.5,22.2-49.5,49.5v52.3v104.8c0,27.3,22.2,49.5,49.5,49.5h157c27.3,0,49.5-22.2,49.5-49.5V374.7
	v-52.3C-15.1,295.2-37.3,273-64.5,273z M-50.3,302.5h5.7v5.6v37.8l-43.3,0.1l-0.1-43.4L-50.3,302.5z M-179.6,374.7
	c8.2-11.3,21.5-18.8,36.5-18.8s28.3,7.4,36.5,18.8c5.4,7.4,8.5,16.5,8.5,26.3c0,24.8-20.2,45.1-45.1,45.1s-44.9-20.3-44.9-45.1
	C-188.1,391.2-184.9,382.1-179.6,374.7z M-40,479.5C-40,493-51,504-64.5,504h-157c-13.5,0-24.5-11-24.5-24.5V374.7h38.2
	c-3.3,8.1-5.2,17-5.2,26.3c0,38.6,31.4,70,70,70c38.6,0,70-31.4,70-70c0-9.3-1.9-18.2-5.2-26.3H-40V479.5z"
      />
    ),
    viewBox: '-271 273 256 256',
  },
  wand: {
    viewBox: '0 0 20 20',
    svg: (
      <>
        <path
          d="M2.33496 14.5715L12.2859 4.62057C12.5036 4.41273 12.793 4.29675 13.094 4.29675C13.3949 4.29675 13.6843 4.41273 13.902 4.62057L15.3797 6.09829C15.5937 6.31277 15.7139 6.60336 15.7139 6.90633C15.7139 7.20929 15.5937 7.49988 15.3797 7.71436L5.42932 17.6653C5.21482 17.8793 4.92424 17.9994 4.62129 17.9994C4.31834 17.9994 4.02775 17.8793 3.81326 17.6653L2.33496 16.1873C2.22882 16.0812 2.14462 15.9553 2.08717 15.8166C2.02972 15.678 2.00015 15.5294 2.00015 15.3793C2.00015 15.2292 2.02972 15.0806 2.08717 14.942C2.14462 14.8033 2.22882 14.6776 2.33496 14.5715ZM14.5717 6.9063L13.094 5.42863L10.2368 8.28579L11.7149 9.76403L14.5721 6.90687L14.5717 6.9063Z"
          fill="#737373"
        />
        <path
          d="M16.8571 8.85715L18 10L16.8571 11.1429L15.7143 10L16.8571 8.85715Z"
          fill="#737373"
        />
        <path
          d="M10.0002 1.99997L11.1431 3.14282L10.0002 4.28567L8.85736 3.14282L10.0002 1.99997Z"
          fill="#737373"
        />
        <path
          d="M16.8571 1.99997L18 3.14282L16.8571 4.28567L15.7143 3.14282L16.8571 1.99997Z"
          fill="#737373"
        />
      </>
    ),
  },
  star: {
    viewBox: '0 0 16 16',
    svg: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.99998 11.8465L12.12 14.3332L11.0266 9.6465L14.6666 6.49317L9.87331 6.0865L7.99998 1.6665L6.12665 6.0865L1.33331 6.49317L4.97331 9.6465L3.87998 14.3332L7.99998 11.8465Z"
      />
    ),
  },
  sparkles: {
    viewBox: '0 0 10 12',
    svg: (
      <>
        <path d="M4.9293 2.50547C4.37124 1.92752 3.98927 0.918281 3.79971 0C3.60986 0.918469 3.2283 1.92792 2.67004 2.50587C2.11198 3.08341 1.13682 3.47885 0.25 3.67549C1.13718 3.87173 2.11216 4.26694 2.67022 4.84491C3.22828 5.42264 3.61025 6.43209 3.8001 7.35038C3.98966 6.43191 4.37151 5.42245 4.92948 4.84451C5.48753 4.26677 6.46269 3.87152 7.34952 3.67488C6.46253 3.47864 5.48756 3.08323 4.9293 2.50547Z" />
        <path d="M8.55419 7.13255C8.22108 6.78753 7.99251 6.18427 7.87927 5.63574C7.76583 6.18427 7.53803 6.78751 7.20456 7.13274C6.87108 7.4776 6.28849 7.71402 5.75873 7.83146C6.28867 7.94888 6.87108 8.18492 7.20456 8.53016C7.53803 8.8752 7.76624 9.47844 7.87966 10.027C7.99288 9.47823 8.22108 8.8752 8.55435 8.52997C8.88765 8.18492 9.47033 7.94867 10 7.83106C9.47036 7.71383 8.88767 7.4776 8.55419 7.13255Z" />
        <path d="M2.64265 8.31348C2.54741 8.77398 2.35615 9.28058 2.07624 9.57034C1.79638 9.85988 1.30723 10.0583 0.862488 10.1569C1.30723 10.2557 1.79638 10.4535 2.07624 10.7435C2.35615 11.0332 2.54779 11.5394 2.64304 12.0001C2.73819 11.5394 2.92954 11.0332 3.20954 10.7433C3.48922 10.4536 3.97846 10.2553 4.42311 10.1565C3.97848 10.0581 3.48922 9.85991 3.20936 9.57015C2.92954 9.2806 2.73781 8.774 2.64265 8.31348Z" />
      </>
    ),
  },
  identification: {
    viewBox: '0 0 20 20',
    svg: (
      <g
        xmlns="http://www.w3.org/2000/svg"
        clipPath="url(#clip0_1353_48331)"
        fill="none"
      >
        <mask id="path-1-inside-1_1353_48331" fill="white">
          <rect x="1.5" y="3" width="17" height="15" rx="1" />
        </mask>
        <rect
          x="1.5"
          y="3"
          width="17"
          height="15"
          rx="1"
          stroke="#363940"
          strokeWidth="3"
          mask="url(#path-1-inside-1_1353_48331)"
        />
        <path
          d="M15.8334 6.66667H4.16675V8.16667H15.8334V6.66667Z"
          fill="#363940"
        />
        <path d="M8.33317 11H4.1665V12.5H8.33317V11Z" fill="#363940" />
        <rect
          x="12.1665"
          y="11.5"
          width="3.16667"
          height="2.33333"
          stroke="#363940"
        />
      </g>
    ),
  },
};

export type SvgReference = {
  defaultColor?: {
    LIGHT: ColorCodes;
    DARK: ColorCodes;
  };
  viewBox: string;
  size: 'XS' | 'S' | 'M' | 'L' | 'XL';
  svg: React.ReactNode;
};

const Presets = {
  xsFiltersRemove: {
    defaultColor: SWEET_ORANGE,
    size: 'XS',
    svg: SvgPaths.remove.svg,
    viewBox: SvgPaths.remove.viewBox,
  },
  xsInputsClose: {
    defaultColor: STORM,
    size: 'XS',
    viewBox: SvgPaths.inputsClose.viewBox,
    svg: SvgPaths.inputsClose.svg,
  },
  sInputsClose: {
    defaultColor: STORM,
    size: 'S',
    viewBox: SvgPaths.inputsClose.viewBox,
    svg: SvgPaths.inputsClose.svg,
  },
  sChevronLeft: {
    defaultColor: SWEET_ORANGE,
    size: 'S',
    viewBox: SvgPaths.chevronLeft.viewBox,
    svg: SvgPaths.chevronLeft.svg,
  },
  sChevronUp: {
    defaultColor: SWEET_ORANGE,
    size: 'S',
    viewBox: SvgPaths.chevronUp.viewBox,
    svg: SvgPaths.chevronUp.svg,
  },
  sChevronDown: {
    defaultColor: SWEET_ORANGE,
    size: 'S',
    viewBox: SvgPaths.chevronDown.viewBox,
    svg: SvgPaths.chevronDown.svg,
  },
  sChevronRight: {
    defaultColor: SWEET_ORANGE,
    size: 'S',
    viewBox: SvgPaths.chevronRight.viewBox,
    svg: SvgPaths.chevronRight.svg,
  },
  sArrowUp: {
    defaultColor: STEEL,
    size: 'S',
    viewBox: SvgPaths.arrowUp.viewBox,
    svg: SvgPaths.arrowUp.svg,
  },
  sArrowDown: {
    defaultColor: STEEL,
    size: 'S',
    viewBox: SvgPaths.arrowDown.viewBox,
    svg: SvgPaths.arrowDown.svg,
  },
  sInputsClear: {
    defaultColor: STORM,
    size: 'S',
    viewBox: SvgPaths.inputsClear.viewBox,
    svg: SvgPaths.inputsClear.svg,
  },
  sActionsEdit: {
    defaultColor: STORM,
    size: 'S',
    viewBox: SvgPaths.actionsEdit.viewBox,
    svg: SvgPaths.actionsEdit.svg,
  },
  sExternal: {
    defaultColor: STORM,
    size: 'S',
    viewBox: SvgPaths.sExternal.viewBox,
    svg: SvgPaths.sExternal.svg,
  },
  sCheck: {
    defaultColor: STORM,
    size: 'S',
    viewBox: SvgPaths.sCheck.viewBox,
    svg: SvgPaths.sCheck.svg,
  },
  sPencil: {
    defaultColor: SWAN,
    size: 'S',
    viewBox: SvgPaths.sPencil.viewBox,
    svg: SvgPaths.sPencil.svg,
  },
  sPlus: {
    defaultColor: SWAN,
    size: 'S',
    viewBox: SvgPaths.sPlus.viewBox,
    svg: SvgPaths.sPlus.svg,
  },
  sCart: {
    defaultColor: STORM,
    size: 'S',
    viewBox: SvgPaths.mCart.viewBox,
    svg: SvgPaths.mCart.svg,
  },
  mLock: {
    defaultColor: SWAN,
    size: 'M',
    viewBox: SvgPaths.mLock.viewBox,
    svg: SvgPaths.mLock.svg,
  },
  lPlayVideo: {
    defaultColor: SWEET_ORANGE,
    size: 'L',
    viewBox: SvgPaths.playVideo.viewBox,
    svg: SvgPaths.playVideo.svg,
  },
  sVideo: {
    defaultColor: SWEET_ORANGE,
    size: 'S',
    viewBox: SvgPaths.video.viewBox,
    svg: SvgPaths.video.svg,
  },
  sDelete: {
    defaultColor: ROSE,
    size: 'S',
    viewBox: SvgPaths.delete.viewBox,
    svg: SvgPaths.delete.svg,
  },
  sPriceTag: {
    defaultColor: STORM,
    size: 'S',
    viewBox: SvgPaths.priceTag.viewBox,
    svg: SvgPaths.priceTag.svg,
  },
  mIdentification: {
    defaultColor: SWAN,
    size: 'M',
    viewBox: SvgPaths.identification.viewBox,
    svg: SvgPaths.identification.svg,
  },
  mDelete: {
    defaultColor: ROSE,
    size: 'M',
    viewBox: SvgPaths.delete.viewBox,
    svg: SvgPaths.delete.svg,
  },
  mFilter: {
    defaultColor: SHADOW,
    size: 'M',
    viewBox: SvgPaths.filter.viewBox,
    svg: SvgPaths.filter.svg,
  },
  sRecurring: {
    defaultColor: SWEET_ORANGE,
    size: 'S',
    viewBox: SvgPaths.recurring.viewBox,
    svg: SvgPaths.recurring.svg,
  },
  sSparkles: {
    defaultColor: SHADOW,
    size: 'S',
    viewBox: SvgPaths.sparkles.viewBox,
    svg: SvgPaths.sparkles.svg,
  },
  mMore: {
    defaultColor: SWEET_ORANGE,
    size: 'M',
    viewBox: SvgPaths.more.viewBox,
    svg: SvgPaths.more.svg,
  },
  mClock: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.mClock.viewBox,
    svg: SvgPaths.mClock.svg,
  },
  mCalendarDays: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.mCalendarDays.viewBox,
    svg: SvgPaths.mCalendarDays.svg,
  },
  mDebit: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.mDebit.viewBox,
    svg: SvgPaths.mDebit.svg,
  },
  mCard: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.mCard.viewBox,
    svg: SvgPaths.mCard.svg,
  },
  lCard: {
    defaultColor: STORM,
    size: 'L',
    viewBox: SvgPaths.mCard.viewBox,
    svg: SvgPaths.mCard.svg,
  },
  mPhone: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.mPhone.viewBox,
    svg: SvgPaths.mPhone.svg,
  },
  mLink: {
    defaultColor: SWEET_ORANGE,
    size: 'M',
    viewBox: SvgPaths.mLink.viewBox,
    svg: SvgPaths.mLink.svg,
  },
  mSync: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.sync.viewBox,
    svg: SvgPaths.sync.svg,
  },
  mStarRating: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.starRating.viewBox,
    svg: SvgPaths.starRating.svg,
  },
  mStar: {
    defaultColor: SHADOW,
    size: 'M',
    viewBox: SvgPaths.star.viewBox,
    svg: SvgPaths.star.svg,
  },
  mPercentage: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.percentage.viewBox,
    svg: SvgPaths.percentage.svg,
  },
  mPriceTag: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.priceTag.viewBox,
    svg: SvgPaths.priceTag.svg,
  },
  mTrash: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.mTrash.viewBox,
    svg: SvgPaths.mTrash.svg,
  },
  mComment: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.mComment.viewBox,
    svg: SvgPaths.mComment.svg,
  },
  mCheck: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.mCheck.viewBox,
    svg: SvgPaths.mCheck.svg,
  },
  lCheck: {
    defaultColor: STORM,
    size: 'L',
    viewBox: SvgPaths.mCheck.viewBox,
    svg: SvgPaths.mCheck.svg,
  },
  mFile: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.mFile.viewBox,
    svg: SvgPaths.mFile.svg,
  },
  mLocation: {
    defaultColor: SWAN,
    size: 'M',
    viewBox: SvgPaths.mLocation.viewBox,
    svg: SvgPaths.mLocation.svg,
  },
  mShopFront: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.mShopFront.viewBox,
    svg: SvgPaths.mShopFront.svg,
  },
  mAddToCart: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.mAddToCart.viewBox,
    svg: SvgPaths.mAddToCart.svg,
  },
  mMinus: {
    defaultColor: SWEET_ORANGE,
    size: 'M',
    viewBox: SvgPaths.mMinus.viewBox,
    svg: SvgPaths.mMinus.svg,
  },
  mActionsEdit: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.actionsEdit.viewBox,
    svg: SvgPaths.actionsEdit.svg,
  },
  mInputsClose: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.inputsClose.viewBox,
    svg: SvgPaths.inputsClose.svg,
  },
  mActionsSearch: {
    defaultColor: SWEET_ORANGE,
    size: 'M',
    viewBox: SvgPaths.actionsSearch.viewBox,
    svg: SvgPaths.actionsSearch.svg,
  },
  mActionsHelp: {
    defaultColor: SWEET_ORANGE,
    size: 'M',
    viewBox: SvgPaths.actionsHelp.viewBox,
    svg: SvgPaths.actionsHelp.svg,
  },
  mActionsEmail: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.mActionsEmail.viewBox,
    svg: SvgPaths.mActionsEmail.svg,
  },
  mOtherPastSearch: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.otherPastSearch.viewBox,
    svg: SvgPaths.otherPastSearch.svg,
  },
  mPencil: {
    defaultColor: SWAN,
    size: 'M',
    svg: SvgPaths.mPencil.svg,
    viewBox: SvgPaths.mPencil.viewBox,
  },
  mKebab: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.mKebab.viewBox,
    svg: SvgPaths.mKebab.svg,
  },
  mCart: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.mCart.viewBox,
    svg: SvgPaths.mCart.svg,
  },
  mDollar: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.mDollar.viewBox,
    svg: SvgPaths.mDollar.svg,
  },
  mWarning: {
    defaultColor: GOLD,
    size: 'M',
    viewBox: SvgPaths.lInputsError.viewBox,
    svg: SvgPaths.lInputsError.svg,
  },
  mAdd: {
    defaultColor: STORM,
    size: 'M',
    svg: SvgPaths.mAdd.svg,
    viewBox: SvgPaths.mAdd.viewBox,
  },
  mActionsFavourite: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.favourites.viewBox,
    svg: SvgPaths.favourites.svg,
  },
  mActionsAttach: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.actionsAttach.viewBox,
    svg: SvgPaths.actionsAttach.svg,
  },
  mUser: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.lUser.viewBox,
    svg: SvgPaths.lUser.svg,
  },
  mDelivery: {
    defaultColor: SWAN,
    size: 'M',
    svg: SvgPaths.mDelivery.svg,
    viewBox: SvgPaths.mDelivery.viewBox,
  },
  mChatDefault: {
    defaultColor: SWAN,
    size: 'M',
    svg: SvgPaths.mChatDefault.svg,
    viewBox: SvgPaths.mChatDefault.viewBox,
  },
  mUsers: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.mUsers.viewBox,
    svg: SvgPaths.mUsers.svg,
  },
  mCircle: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.mCircle.viewBox,
    svg: SvgPaths.mCircle.svg,
  },
  mQrCode: {
    defaultColor: SWEET_ORANGE,
    size: 'M',
    viewBox: SvgPaths.qrCode.viewBox,
    svg: SvgPaths.qrCode.svg,
  },
  mListBullet: {
    defaultColor: SWAN,
    size: 'M',
    svg: SvgPaths.lListBullet.svg,
    viewBox: SvgPaths.lListBullet.viewBox,
  },
  mActionsSend: {
    defaultColor: SWAN,
    size: 'M',
    svg: SvgPaths.actionsSend.svg,
    viewBox: SvgPaths.actionsSend.viewBox,
  },
  mTag: {
    defaultColor: SWAN,
    size: 'M',
    svg: SvgPaths.tag.svg,
    viewBox: SvgPaths.tag.viewBox,
  },
  mActionsCopy: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.mActionsCopy.viewbox,
    svg: SvgPaths.mActionsCopy.svg,
  },
  mArrowLeft: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.ArrowLeft.viewBox,
    svg: SvgPaths.ArrowLeft.svg,
  },
  xlActionsFavourite: {
    defaultColor: STORM,
    size: 'XL',
    viewBox: SvgPaths.favourites.viewBox,
    svg: SvgPaths.favourites.svg,
  },
  lSync: {
    defaultColor: STORM,
    size: 'L',
    viewBox: SvgPaths.lSync.viewBox,
    svg: SvgPaths.lSync.svg,
  },
  lAddToCart: {
    defaultColor: STORM,
    size: 'L',
    viewBox: SvgPaths.lAddToCart.viewBox,
    svg: SvgPaths.lAddToCart.svg,
  },
  lMenu: {
    defaultColor: STORM,
    size: 'L',
    viewBox: SvgPaths.lMenu.viewBox,
    svg: SvgPaths.lMenu.svg,
  },
  lDiscover: {
    defaultColor: STORM,
    size: 'L',
    viewBox: SvgPaths.lDiscover.viewBox,
    svg: SvgPaths.lDiscover.svg,
  },
  lCart: {
    defaultColor: STORM,
    size: 'L',
    viewBox: SvgPaths.lCart.viewBox,
    svg: SvgPaths.lCart.svg,
  },
  lActionsCopy: {
    defaultColor: STORM,
    size: 'L',
    viewBox: SvgPaths.actionsCopy.viewbox,
    svg: SvgPaths.actionsCopy.svg,
  },
  lActionsSearch: {
    defaultColor: STORM,
    size: 'L',
    viewBox: SvgPaths.lSearch.viewBox,
    svg: SvgPaths.lSearch.svg,
  },
  lActionsAttach: {
    defaultColor: STORM,
    size: 'L',
    viewBox: SvgPaths.actionsAttach.viewBox,
    svg: SvgPaths.actionsAttach.svg,
  },
  lUser: {
    defaultColor: STORM,
    size: 'L',
    viewBox: SvgPaths.lUser.viewBox,
    svg: SvgPaths.lUser.svg,
  },
  lHelp: {
    defaultColor: STORM,
    size: 'L',
    viewBox: SvgPaths.lHelp.viewBox,
    svg: SvgPaths.lHelp.svg,
  },
  lLock: {
    defaultColor: STORM,
    size: 'L',
    viewBox: SvgPaths.lLock.viewBox,
    svg: SvgPaths.lLock.svg,
  },
  lShopFront: {
    defaultColor: STORM,
    size: 'L',
    viewBox: SvgPaths.lShopFront.viewBox,
    svg: SvgPaths.lShopFront.svg,
  },
  lCalendarDays: {
    defaultColor: STORM,
    size: 'L',
    viewBox: SvgPaths.lCalendarDays.viewBox,
    svg: SvgPaths.lCalendarDays.svg,
  },
  lArrowLeft: {
    defaultColor: STORM,
    size: 'L',
    viewBox: SvgPaths.ArrowLeft.viewBox,
    svg: SvgPaths.ArrowLeft.svg,
  },
  lHome: {
    defaultColor: STORM,
    size: 'L',
    viewBox: SvgPaths.lHome.viewBox,
    svg: SvgPaths.lHome.svg,
  },
  sActionsInfo: {
    defaultColor: STORM,
    size: 'S',
    viewBox: SvgPaths.mActionsInfo.viewBox,
    svg: SvgPaths.mActionsInfo.svg,
  },
  mActionsInfo: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.mActionsInfo.viewBox,
    svg: SvgPaths.mActionsInfo.svg,
  },
  lActionsInfo: {
    defaultColor: STORM,
    size: 'L',
    viewBox: SvgPaths.mActionsInfo.viewBox,
    svg: SvgPaths.mActionsInfo.svg,
  },
  lActionsFavourite: {
    defaultColor: STORM,
    size: 'L',
    viewBox: SvgPaths.favourites.viewBox,
    svg: SvgPaths.favourites.svg,
  },
  lIconWifi: {
    defaultColor: STORM,
    size: 'L',
    svg: SvgPaths.lIconWifi.svg,
    viewBox: SvgPaths.lIconWifi.viewBox,
  },
  lIconCombinedShape: {
    defaultColor: STORM,
    size: 'L',
    svg: SvgPaths.lIconCombinedShape.svg,
    viewBox: SvgPaths.lIconCombinedShape.viewBox,
  },
  lAdd: {
    defaultColor: STORM,
    size: 'L',
    svg: SvgPaths.lAdd.svg,
    viewBox: SvgPaths.lAdd.viewBox,
  },
  lIconBattery: {
    defaultColor: STORM,
    size: 'L',
    svg: SvgPaths.lIconBattery.svg,
    viewBox: SvgPaths.lIconBattery.viewBox,
  },
  lPencil: {
    defaultColor: SWAN,
    size: 'L',
    svg: SvgPaths.lPencil.svg,
    viewBox: SvgPaths.lPencil.viewBox,
  },
  lPlus: {
    defaultColor: SWAN,
    size: 'L',
    svg: SvgPaths.lPlus.svg,
    viewBox: SvgPaths.lPlus.viewBox,
  },
  lActionsSend: {
    defaultColor: SWAN,
    size: 'L',
    svg: SvgPaths.actionsSend.svg,
    viewBox: SvgPaths.actionsSend.viewBox,
  },
  mPlus: {
    defaultColor: SWAN,
    size: 'M',
    svg: SvgPaths.lPlus.svg,
    viewBox: SvgPaths.lPlus.viewBox,
  },
  mSettings: {
    defaultColor: SWAN,
    size: 'M',
    svg: SvgPaths.mSettings.svg,
    viewBox: SvgPaths.mSettings.viewBox,
  },
  lSettings: {
    defaultColor: SWAN,
    size: 'L',
    svg: SvgPaths.lSettings.svg,
    viewBox: SvgPaths.lSettings.viewBox,
  },
  lChatDefault: {
    defaultColor: SWAN,
    size: 'L',
    svg: SvgPaths.lChatDefault.svg,
    viewBox: SvgPaths.lChatDefault.viewBox,
  },
  lCartDefault: {
    defaultColor: SWAN,
    size: 'L',
    svg: SvgPaths.lCartDefault.svg,
    viewBox: SvgPaths.lCartDefault.viewBox,
  },
  sMenu: {
    defaultColor: SWAN,
    size: 'S',
    svg: SvgPaths.sMenu.svg,
    viewBox: SvgPaths.sMenu.viewBox,
  },
  lUsers: {
    defaultColor: SWAN,
    size: 'L',
    svg: SvgPaths.lUsers.svg,
    viewBox: SvgPaths.lUsers.viewBox,
  },
  lGraph: {
    defaultColor: SWAN,
    size: 'L',
    svg: SvgPaths.lGraph.svg,
    viewBox: SvgPaths.lGraph.viewBox,
  },
  lDollar: {
    defaultColor: SWAN,
    size: 'L',
    svg: SvgPaths.lDollar.svg,
    viewBox: SvgPaths.lDollar.viewBox,
  },
  lFile: {
    defaultColor: SWAN,
    size: 'L',
    svg: SvgPaths.lFile.svg,
    viewBox: SvgPaths.lFile.viewBox,
  },
  lUserAdd: {
    defaultColor: SWAN,
    size: 'L',
    svg: SvgPaths.lUserAdd.svg,
    viewBox: SvgPaths.lUserAdd.viewBox,
  },
  lNavigationBack: {
    defaultColor: STORM,
    size: 'L',
    svg: SvgPaths.lNavigationBack.svg,
    viewBox: SvgPaths.lNavigationBack.viewBox,
  },
  lListBullet: {
    defaultColor: SWAN,
    size: 'L',
    svg: SvgPaths.lListBullet.svg,
    viewBox: SvgPaths.lListBullet.viewBox,
  },
  lTag: {
    defaultColor: SWAN,
    size: 'L',
    svg: SvgPaths.tag.svg,
    viewBox: SvgPaths.tag.viewBox,
  },
  lInsights: {
    defaultColor: SWAN,
    size: 'L',
    svg: SvgPaths.lInsights.svg,
    viewBox: SvgPaths.lInsights.viewBox,
  },
  lClose: {
    defaultColor: STORM,
    size: 'L',
    svg: SvgPaths.lClose.svg,
    viewBox: SvgPaths.lClose.viewBox,
  },
  lInputsInfo: {
    defaultColor: STORM,
    size: 'L',
    svg: SvgPaths.lInputsInfo.svg,
    viewBox: SvgPaths.lInputsInfo.viewBox,
  },
  lInputsSuccess: {
    defaultColor: CLOVER,
    size: 'L',
    svg: SvgPaths.lInputsSuccess.svg,
    viewBox: SvgPaths.lInputsSuccess.viewBox,
  },
  lInputsError: {
    defaultColor: ROSE,
    size: 'L',
    svg: SvgPaths.lInputsError.svg,
    viewBox: SvgPaths.lInputsError.viewBox,
  },
  lActionsHelp: {
    defaultColor: SWEET_ORANGE,
    size: 'L',
    viewBox: SvgPaths.actionsHelp.viewBox,
    svg: SvgPaths.actionsHelp.svg,
  },
  lQrCode: {
    defaultColor: SWEET_ORANGE,
    size: 'L',
    viewBox: SvgPaths.qrCode.viewBox,
    svg: SvgPaths.qrCode.svg,
  },
  lResolved: {
    defaultColor: SWEET_ORANGE,
    size: 'L',
    svg: SvgPaths.resolved.svg,
    viewBox: SvgPaths.resolved.viewBox,
  },
  lTrophy: {
    defaultColor: SWAN,
    size: 'L',
    svg: SvgPaths.trophy.svg,
    viewBox: SvgPaths.trophy.viewBox,
  },
  mTrophy: {
    defaultColor: SWAN,
    size: 'M',
    svg: SvgPaths.trophy.svg,
    viewBox: SvgPaths.trophy.viewBox,
  },
  lWarning: {
    defaultColor: SWEET_ORANGE,
    size: 'L',
    viewBox: SvgPaths.warning.viewBox,
    svg: SvgPaths.warning.viewBox,
  },
  lDownload: {
    defaultColor: SWAN,
    size: 'L',
    svg: SvgPaths.download.svg,
    viewBox: SvgPaths.download.viewBox,
  },
  mDownload: {
    defaultColor: SWAN,
    size: 'M',
    svg: SvgPaths.download.svg,
    viewBox: SvgPaths.download.viewBox,
  },
  mShare: {
    defaultColor: SWEET_ORANGE,
    size: 'M',
    svg: SvgPaths.mShare.svg,
    viewBox: SvgPaths.mShare.viewBox,
  },
  mDirectDebit: {
    defaultColor: SWAN,
    size: 'M',
    svg: SvgPaths.directDebit.svg,
    viewBox: SvgPaths.directDebit.viewBox,
  },
  mTick: {
    defaultColor: STORM,
    size: 'M',
    viewBox: SvgPaths.tick.viewBox,
    svg: SvgPaths.tick.svg,
  },
  mDoubleTick: {
    defaultColor: STEEL,
    size: 'M',
    svg: SvgPaths.doubleTick.svg,
    viewBox: SvgPaths.doubleTick.viewBox,
  },
  mDrag: {
    defaultColor: STORM,
    size: 'M',
    svg: SvgPaths.drag.svg,
    viewBox: SvgPaths.drag.viewBox,
  },
  mSort: {
    defaultColor: STORM,
    size: 'M',
    svg: SvgPaths.sort.svg,
    viewBox: SvgPaths.sort.viewBox,
  },
  lPrint: {
    defaultColor: SWEET_ORANGE,
    size: 'L',
    svg: SvgPaths.print.svg,
    viewBox: SvgPaths.print.viewBox,
  },
  googleIcon: {
    size: 'L',
    svg: SvgPaths.googleIcon.svg,
    viewBox: SvgPaths.googleIcon.viewBox,
  },
  appleIcon: {
    size: 'L',
    svg: SvgPaths.appleIcon.svg,
    viewBox: SvgPaths.appleIcon.viewBox,
  },
  facebookIcon: {
    size: 'M',
    svg: SvgPaths.facebookIcon.svg,
    viewBox: SvgPaths.facebookIcon.viewBox,
  },
  twitterIcon: {
    size: 'M',
    svg: SvgPaths.twitterIcon.svg,
    viewBox: SvgPaths.twitterIcon.viewBox,
  },
  instagramIcon: {
    size: 'M',
    svg: SvgPaths.instagramIcon.svg,
    viewBox: SvgPaths.instagramIcon.viewBox,
  },
  wandIcon: {
    size: 'S',
    svg: SvgPaths.wand.svg,
    viewBox: SvgPaths.wand.viewBox,
  },
  xlWarning: {
    defaultColor: GOLD,
    size: 'XL',
    viewBox: SvgPaths.lInputsError.viewBox,
    svg: SvgPaths.lInputsError.svg,
  },
};

export type SvgNames = keyof typeof Presets;

export const SVG_SIZES = {
  XS: { width: '12', height: '12' },
  S: { width: '16', height: '16' },
  M: { width: '20', height: '20' },
  L: { width: '24', height: '24' },
  XL: { width: '44', height: '44' },
};

export default Presets as { [name in SvgNames]: SvgReference };
